import { drawAvatar, cleanLook, hasLure, type Look } from './avatar';
import { B, BLOCKS, Blocks, blockByItem, type TileEdit } from './blocks';
import { decorReach, drawChest, drawDecor, drawGate, drawPearl, drawSign } from './decor';
import { HOUSES, drawHouse, houseDoor, type HouseKind } from './houses';
import { fishWhere } from './interior';
import { sfxDefeat, sfxFaint, sfxHit, sfxHurt, sfxRare, type Mood } from './sfx';
import { nightness } from './daynight';
import { drawNpc, nextQuest, type NpcId } from './npcs';
import { habitatAt, insideCentre, HABITAT_INFO, type HabitatStatus } from './habitats';
import { BEASTS, BEAST_LINE, beastFor, drawBeast, drawWeapon, toWire, type Beast, type BeastEvent, type BeastKind, type BeastsMsg } from './beasts';
import { REGION, regionOf, type PeerMsg, type Pose } from './live';
import type { AreaId, Chest, Decor, Pearl, Sign, World } from './world';
import { drawIdle, type Sprite } from '../lib/fishIdle';
import { RARITY, SPECIES, speciesById, speciesCanvas } from '../lib/aquarium';

// ─────────────────────────────────────────────────────────────────────────────
// The ocean sandbox: swimming, catching wild fish with a net, mining and
// placing blocks, fish houses, discoveries, pearls and signs. React owns
// everything around it (the HUD, the shop, saving and sand dollars); the
// engine reports what the player did through `events`, and React confirms it
// with the database (or calls revert()).
//
//   play      a student's fish, steered by keys or by holding the pointer
//   spectate  the teacher's view: drag to look around, wheel or buttons to zoom
// ─────────────────────────────────────────────────────────────────────────────

const INK = '#1e2633';
const CHUNK = 16;                 // blocks per side of a cached chunk
const R = 22;                     // the character's collision radius
const MAX_SPEED = 330;
const ACCEL = 1500;
const DRAG = 2.4;
const REACH = 150;                // how far away you can mine or build

/** A hurt Kraken remembers its health this long (s): faint, swim back, and the fight goes on. */
const KRAKEN_MEMORY = 15 * 60;
/** After it's beaten, the Kraken rests this long (s) before it can rise again. */
const KRAKEN_REST = 5 * 60;
/** Beasts appear outside this box around every player: wider than anyone's screen (play zoom shows about ±770 × ±490, ±870 tall on a portrait tablet). */
const SPAWN_CLEAR = { x: 1100, y: 900 };

export type HouseFish = { id: string; species: string };
/** A fish house somewhere in the ocean; `owner` is a name only when the class shows names. */
/** Fish a bucket holds; the server enforces it too (ocean_catch). */
export const BUCKET_SIZE = 3;

export type OceanHouse = { id: string; kind: HouseKind; x: number; y: number; mine: boolean; owner: string | null; student_id?: string | null; interior?: unknown; fish: HouseFish[] };
export type OceanMe = {
  student_id: string; look: unknown; areas: string[]; pearls: string[];
  balance: number; inventory: Record<string, number>; bucket: HouseFish[];
  /** Treasure chests this student has opened. */
  chests?: string[];
  /** Species in their Fishpedia. */
  pedia?: { species: string; how: 'caught' | 'attracted' | 'hatched' }[];
  /** Temple gates they've opened (gate0, gate1). */
  gates?: string[];
  /** Quests they've done (game/npcs.ts). */
  quests?: string[];
};
/** A Fish beacon someone placed, and the fish that have moved in. */
export type OceanHabitat = { tx: number; ty: number; owner: string | null; mine: boolean; residents: HouseFish[] };
export type OceanData = { /** Homework first: no assignment done today yet (ocean_world). */ locked?: boolean; names: boolean; houses: OceanHouse[]; people: { id: string; name: string }[]; me: OceanMe | null; habitats?: OceanHabitat[] };

export type Tool = { kind: 'net' } | { kind: 'pick' } | { kind: 'sword' } | { kind: 'block'; item: string } | { kind: 'house'; item: HouseKind };
export type Target = { kind: 'sign'; i: number } | { kind: 'house'; id: string } | { kind: 'chest'; id: string; tier: 1 | 2 | 3 } | { kind: 'beacon'; tx: number; ty: number } | { kind: 'gate'; i: number } | { kind: 'npc'; id: NpcId } | null;

export type OceanEvents = {
  onTarget?: (t: Target) => void;
  onArea?: (id: AreaId, isNew: boolean) => void;
  onPearl?: (id: string) => void;
  onInteract?: (t: Target) => void;
  /** Open a treasure chest (it shows as opened only once the database says so: markChest). */
  onOpenChest?: (id: string, tier: 1 | 2 | 3) => void;
  /** Near a habitat that's closed in and has a type: time to see if a fish moves in. */
  onHabitatVisit?: (tx: number, ty: number, status: HabitatStatus) => void;
  /** The minimap uncovered another stretch of the map (an index into world.bounds). */
  onReveal?: (i: number) => void;
  /** Teacher's map: a house was tapped. */
  onHouseClick?: (id: string) => void;
  /** The player mined a block (already removed on screen; call revert() if the database says no). */
  onMine?: (tx: number, ty: number, item: string) => void;
  /** The player placed a block from their inventory (already shown). */
  onBuild?: (tx: number, ty: number, item: string) => void;
  onCatch?: (species: string) => void;
  /** Place a house standing on the ground at (x, y). */
  onPlaceHouse?: (kind: HouseKind, x: number, y: number) => void;
  /** Something the player tried that can't happen (e.g. "You need a Drill"). */
  onNotice?: (text: string) => void;
  /** Lives left (0–3) changed. */
  onLives?: (lives: number, hitBy: BeastKind | null) => void;
  /** All three lives lost: the player is already back at the start. */
  onFaint?: (by: BeastKind) => void;
  /** A swing hit at least one beast: one use of that weapon is gone. */
  onWeaponHit?: (weapon: 'sword' | 'trident') => void;
  onBeastDefeated?: (kind: BeastKind) => void;
  /** A beast I was running died (anyone's hit): for the class's goals. */
  onBeastDied?: (kind: BeastKind) => void;
  /** An epic or legendary fish turned up nearby. */
  onRareFish?: (species: string) => void;
  /** The Kraken rose. */
  onBoss?: (kind: BeastKind) => void;
  /** Share my region's beasts with classmates (I'm running them). */
  onBeastSync?: (m: BeastsMsg) => void;
  /** Tell whoever runs the beasts that one bit me or I hit it. */
  onBeastEvent?: (e: BeastEvent) => void;
};

// ── Fish art, shared by every engine ───────────────────────────────────────
const sprites = new Map<string, Sprite | 'loading'>();
export function spriteFor(species: string): Sprite | null {
  const id = speciesById(species).id;
  let sp = sprites.get(id);
  if (!sp || sp === 'loading') {
    const c = speciesCanvas(id);
    sp = { canvas: c, w: c.width, h: c.height };
    sprites.set(id, sp);
  }
  return sp;
}

function hash(s: string, salt = 0): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return ((h >>> 0) % 100000) / 100000;
}
function cellHash(tx: number, ty: number, salt: number): number {
  let h = (tx * 374761393 + ty * 668265263 + salt * 1442695041) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// Far-off hills for depth, drawn slower than the camera.
const HILLS: [number, number][] = Array.from({ length: 220 }, (_, i) => {
  const x = i * 120 - 400;
  return [x, 900 + Math.sin(i * 0.7) * 120 + Math.sin(i * 0.23 + 1) * 160];
});

// Wild fish: each species lives in some areas (lib/aquarium.ts) and turns up as
// often as its rarity says. Every cave chamber counts as 'caves'.
const CAVE_AREAS = new Set<string>(['caves', 'grotto', 'echo', 'hollow']);

/** A classmate swimming in the ocean right now (from the live channel). */
type Peer = { id: string; look: Look | null; x: number; y: number; tx: number; ty: number; vx: number; vy: number; facing: 1 | -1; effort: number; seen: number; gone: number };

type Wild = { id: number; species: string; x: number; y: number; vx: number; vy: number; turn: number; dash: number; born: number };
type View = { x0: number; x1: number; y0: number; y1: number };

export class Ocean {
  events: OceanEvents = {};
  readonly blocks: Blocks;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private mode: 'play' | 'spectate';
  private dpr = 1;
  private vw = 0;
  private vh = 0;
  private world: World;
  private cam = { x: 0, y: 0, z: 1 };
  private me = { x: 0, y: 0, vx: 0, vy: 0, facing: 1 as 1 | -1, effort: 0 };
  private look: Look | null = null;
  private data: OceanData = { names: true, houses: [], people: [], me: null };
  private names = new Map<string, string>();
  private known = new Set<string>();
  /** Stretches of the minimap uncovered (indexes into world.bounds). */
  private revealed = new Set<number>();
  private opened = new Set<string>();
  private chests: Chest[] = [];
  /** Beacons on the map, and what each habitat is like (kept until a block changes). */
  private beacons: [number, number][] = [];
  private beaconVer = -1;
  private habitatCache = new Map<string, { ver: number; s: HabitatStatus }>();
  private visits = new Map<string, number>();
  private visitCheck = 0;
  private pearlsHad = new Set<string>();
  private keys = new Set<string>();
  private pointer: { id: number; x: number; y: number; sx: number; sy: number; t: number; moved: boolean; camX: number; camY: number } | null = null;
  private hover: { x: number; y: number } | null = null;
  private tool: Tool = { kind: 'net' };
  /** What the player owns that changes what they can do; pickaxe/hammer are uses left. */
  private gear = { drill: false, goldenNet: false, pickaxe: 0, hammer: 0, sword: 0, trident: 0 };
  // Sea beasts and your three lives.
  private beasts: Beast[] = [];
  private beastId = 0;
  private myId: string | null = null;
  private beastRegion = -1;
  private leader: string | null = null;
  private leaderAt = 0;
  private broadcasting = false;
  private beastSend = 0;
  private deadQueue: NonNullable<BeastsMsg['dead']> = [];
  private biteCool = new Map<string, number>();
  /** The teacher's map: every region's beasts, as their runners share them. */
  private remote = new Map<number, { at: number; list: Beast[] }>();
  private beastTimer = 2;
  private lives = 3;
  private invuln = 0;
  /** Seconds left of being knocked back after a bite (you can't swim against it). */
  private knock = 0;
  /** Seconds left of the red flash after a bite. */
  private flash = 0;
  /** Camera shake (seconds left, strength) and hit-stop (a tiny freeze when a blow lands). */
  private shakeT = 0;
  private shakeAmp = 0;
  private stopT = 0;
  /** Bubbles trailing a fast fish, and puffs of sand and water. */
  private bubbles: { x: number; y: number; vx: number; vy: number; r: number; t: number }[] = [];
  private puffs: { x: number; y: number; t: number; color: string; r: number }[] = [];
  private bubbleClock = 0;
  private rareSeen = 0;
  /** The Kraken's entrance: the camera swings over to it for a moment. */
  private cine: { x: number; y: number; t: number } | null = null;
  private bossSeen = new Set<string>();
  /** When each beast first showed up on MY screen: it fades in and can't bite for a moment (no beasts out of nowhere). */
  private appear = new Map<string, number>();
  /** Each temple's Kraken: its health while a fight is on (fainting and swimming back doesn't heal it), and when it was beaten. */
  private krakenMemo = new Map<number, { hp: number; max: number; at: number; beaten?: number }>();
  /** Gates this student has opened. */
  private gatesOpen = new Set<string>();
  private questsDone = new Set<string>();
  /** Emotes over fish: who, which, when. */
  private emotes = new Map<string, { e: string; t: number }>();
  private flashCanvas: HTMLCanvasElement | null = null;
  private sinceHit = 0;
  private blade = { swing: -1, cooldown: 0, hit: false };
  private target: Target = null;
  private area: AreaId | null = null;
  private areaCheck = 0;
  private chunks = new Map<string, { c: HTMLCanvasElement; ver: number; used: number }>();
  private chunkScale = 1;
  private minimap: HTMLCanvasElement | null = null;
  private minimapBase: HTMLCanvasElement | null = null;
  private minimapVer = -1;
  private editVer = 0;
  private miniTick = 0;
  private sparkles: { x: number; y: number; t: number; color?: string }[] = [];
  private debris: { x: number; y: number; vx: number; vy: number; t: number; color: string }[] = [];
  private wild: Wild[] = [];
  private peers = new Map<string, Peer>();
  private wildId = 0;
  private spawnTimer = 0;
  private net = { swing: -1, cooldown: 0, caught: false };
  private raf = 0;
  private last = 0;
  private time = 0;
  private running = false;
  private cleanup: (() => void)[] = [];
  private darkness: HTMLCanvasElement = document.createElement('canvas');
  private userView = false;
  private failed = false;
  // Things that sit on the ground, snapped to the blocks (and hidden if their block is mined).
  private decor: (Decor & { cell: [number, number] | null })[] = [];
  private signs: Sign[] = [];
  private npcs: { id: NpcId; x: number; y: number }[] = [];
  private pearls: Pearl[] = [];

  constructor(canvas: HTMLCanvasElement, mode: 'play' | 'spectate', world: World, events: OceanEvents = {}) {
    this.world = world;
    this.blocks = new Blocks(world);
    this.cam.x = this.me.x = world.spawn.x;
    this.cam.y = this.me.y = world.spawn.y;
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.mode = mode;
    this.events = events;
    this.snapToBlocks();
    this.resize();
    const ro = new ResizeObserver(() => this.resize());
    ro.observe(canvas);
    this.cleanup.push(() => ro.disconnect());
    this.bindInput();
    if (mode === 'spectate') this.fit();
    const onVis = () => { if (document.hidden) this.pause(); else this.start(); };
    document.addEventListener('visibilitychange', onVis);
    this.cleanup.push(() => document.removeEventListener('visibilitychange', onVis));
    this.start();
  }

  destroy() {
    this.pause();
    for (const f of this.cleanup) f();
    this.chunks.clear();
  }

  /** Sit decorations, signs, pearls and spots on the block surface. */
  private snapToBlocks() {
    const bl = this.blocks;
    this.decor = this.world.decor.map(d => {
      if (d.up) {
        const y = bl.ceilingAbove(d.x, d.y + 40);
        return { ...d, y: y ?? d.y, cell: y == null ? null : [Math.floor(d.x / B), Math.floor(y / B) - 1] as [number, number] };
      }
      const y = bl.groundBelow(d.x, d.y - 56);
      return { ...d, y: y == null ? d.y : y + 3, cell: y == null ? null : [Math.floor(d.x / B), Math.floor(y / B)] as [number, number] };
    });
    this.signs = this.world.signs.map(s => ({ ...s, y: (bl.groundBelow(s.x, s.y - 56) ?? s.y) + 2 }));
    this.npcs = this.world.npcs.map(n => ({ ...n, y: (bl.groundBelow(n.x, n.y - 60) ?? n.y) + 2 }));
    this.chests = this.world.chests.map(c => ({ ...c, y: bl.groundBelow(c.x, c.y - 30) ?? c.y }));
    this.pearls = this.world.pearls.map(p => {
      const g = bl.groundBelow(p.x, p.y - 8);
      return g != null && g - p.y < 60 ? { ...p, y: g - 16 } : p;
    });
  }

  // ── Data from React ─────────────────────────────────────────────────────
  setData(d: OceanData) {
    const first = !this.data.me && !!d.me;
    this.data = d;
    this.names = new Map(d.people.map(p => [p.id, p.name]));
    for (const gte of d.me?.gates ?? []) this.gatesOpen.add(gte);
    this.questsDone = new Set(d.me?.quests ?? []);
    if (d.me) {
      for (const a of d.me.areas) this.known.add(a);
      for (const c of d.me.chests ?? []) this.opened.add(c);
      for (const p of d.me.pearls) this.pearlsHad.add(p);
      if (first) {
        const home = this.myHome();
        if (home) this.goTo(home.x, home.y);
      }
    }
    this.miniTick = 0;
  }
  /** The class's block changes. `full` = this is every change (start fresh); otherwise just new ones. */
  setEdits(edits: TileEdit[], full: boolean) {
    if (full) this.blocks.reset();
    this.blocks.apply(edits);
    this.editVer++;
  }
  /** Undo a change the database refused. */
  revert(tx: number, ty: number, tile: number) { this.blocks.set(tx, ty, tile); this.editVer++; }
  setLook(l: Look | null) { this.look = l; }
  setTool(t: Tool) { this.tool = t; }
  setGear(g: { drill: boolean; goldenNet: boolean; pickaxe: number; hammer: number; sword?: number; trident?: number }) { this.gear = { sword: 0, trident: 0, ...g }; }
  /** The on-screen joystick (phones and tablets): a direction, each part -1 to 1; (0, 0) when let go. */
  setStick(x: number, y: number) { this.stick = { x, y }; }
  private stick = { x: 0, y: 0 };
  /** The on-screen action button: swing the net or sword, or mine/build/place in front. */
  act() {
    const k = this.tool.kind;
    if (k === 'net') this.swingNet();
    else if (k === 'sword') this.swingSword();
    else this.useInFront();
  }
  /** A gate this student read the inscription of. */
  openGate(i: number) { this.gatesOpen.add(`gate${i}`); this.burst(this.world.gates[i].x + this.world.gates[i].w / 2, this.world.gates[i].y + 50, '#9ff7ff', 24); }
  /** Show an emote over a fish (mine: 'me'). */
  emote(id: string, e: string) { this.emotes.set(id, { e, t: this.time }); }
  /** The Kraken near me (for the health bar), or null. */
  boss(): { name: string; hp: number; max: number } | null {
    const k = this.beasts.find(b => b.kind === 'kraken' && Math.hypot(b.x - this.me.x, b.y - this.me.y) < 1200);
    return k ? { name: 'The Kraken', hp: Math.max(0, k.hp), max: k.max ?? BEASTS.kraken.hp } : null;
  }
  /** What the place sounds like (sfx.ts setMood). */
  mood(): Mood {
    if (this.boss()) return 'boss';
    const a = this.area;
    if (a === 'temple' || a === 'abyss') return 'temple';
    if (a === 'caves' || a === 'grotto' || a === 'echo' || a === 'hollow') return 'cave';
    if (a === 'wreck' || a === 'cabin') return 'wreck';
    if (a === 'trench' || this.me.y > 2200) return 'deep';
    return 'surface';
  }
  /** A small burst of sparkles somewhere. */
  private burst(x: number, y: number, color: string, n = 12) {
    for (let i = 0; i < n; i++) this.sparkles.push({ x: x + (Math.random() - 0.5) * 60, y: y + (Math.random() - 0.5) * 50, t: this.time - Math.random() * 0.3, color });
  }
  private shake(amp: number, t: number, stop = 0) {
    this.shakeAmp = Math.max(this.shakeT > 0 ? this.shakeAmp : 0, amp);
    this.shakeT = Math.max(this.shakeT, t);
    this.stopT = Math.max(this.stopT, stop);
  }
  /** Locked temple gates are solid (for this student). */
  private gateHits(x: number, y: number, r: number) {
    for (const gt of this.world.gates) {
      if (this.gatesOpen.has(`gate${gt.i}`)) continue;
      const cx = Math.max(gt.x, Math.min(x, gt.x + gt.w)), cy = Math.max(gt.y, Math.min(y, gt.y + gt.h));
      if (Math.hypot(x - cx, y - cy) < r) return true;
    }
    return false;
  }
  /** A chest the database says this student has opened. */
  markChest(id: string) { this.opened.add(id); }
  /** Minimap stretches uncovered before (from this browser). */
  setRevealed(ids: number[]) { for (const i of ids) this.revealed.add(i); this.miniTick = 0; }
  /** What the habitat around a beacon is like now. */
  habitat(tx: number, ty: number): HabitatStatus {
    const key = `${tx},${ty}`, hit = this.habitatCache.get(key);
    if (hit && hit.ver === this.blocks.version) return hit.s;
    const s = habitatAt(this.blocks, tx, ty);
    this.habitatCache.set(key, { ver: this.blocks.version, s });
    return s;
  }
  private beaconList() {
    if (this.beaconVer !== this.blocks.version) {
      this.beaconVer = this.blocks.version;
      this.beacons = [];
      for (const [i, t] of this.blocks.placed) if (t === 13) this.beacons.push([i % this.blocks.cols, Math.floor(i / this.blocks.cols)]);
    }
    return this.beacons;
  }
  /** Who I am, so classmates' screens can agree on the sea beasts. */
  setMe(id: string | null) { this.myId = id; }
  /** Lives carried over (e.g. from before a reload). */
  setLives(n: number) { this.lives = Math.max(1, Math.min(3, Math.round(n))); this.events.onLives?.(this.lives, null); }
  /** Swing your sword (or trident) at sea beasts. */
  swingSword() {
    if (this.mode !== 'play' || this.blade.cooldown > 0) return;
    if (this.gear.sword + this.gear.trident <= 0) { this.events.onNotice?.('You need a sword to fight sea beasts. Buy one in the shop (C).'); return; }
    this.blade = { swing: 0, cooldown: 0.45, hit: false };
  }
  /** How many fish are in the bucket; at BUCKET_SIZE the net can't catch any more. */
  setBucket(n: number) { this.bucket = n; }
  /** While a room or panel is open over the ocean, the keys belong to it. */
  setPaused(p: boolean) { this.paused = p; if (p) this.keys.clear(); }
  private paused = false;
  private bucket = 0;
  setMinimap(c: HTMLCanvasElement | null) { this.minimap = c; this.miniTick = 0; }
  /** Just above your newest house, if you have one. */
  myHome(): { x: number; y: number } | null {
    const mine = this.data.houses.filter(h => h.mine);
    const h = mine[mine.length - 1];
    return h ? { x: h.x, y: h.y - HOUSES[h.kind].size - 90 } : null;
  }
  goTo(x: number, y: number) {
    this.me.x = x; this.me.y = y; this.me.vx = 0; this.me.vy = 0;
    for (let k = 0; k < 60 && this.blocks.circleHits(this.me.x, this.me.y, R); k++) this.me.y -= 10;
    this.cam.x = this.me.x; this.cam.y = this.me.y;
    this.clampCam();
  }
  interact() { if (this.target) this.events.onInteract?.(this.target); }

  // ── Classmates, live ────────────────────────────────────────────────────
  /** Where my fish is, for sending to classmates. */
  pose(): Pose { const m = this.me; return { x: m.x, y: m.y, vx: m.vx, vy: m.vy, f: m.facing, e: m.effort }; }
  /** A classmate's latest position (and, now and then, their look and spot). */
  peerUpdate(msg: PeerMsg) {
    let p = this.peers.get(msg.id);
    if (!p) {
      p = { id: msg.id, look: null, x: msg.p.x, y: msg.p.y, tx: msg.p.x, ty: msg.p.y, vx: 0, vy: 0, facing: msg.p.f, effort: 0, seen: this.time, gone: 0 };
      this.peers.set(msg.id, p);
    }
    if (msg.look !== undefined) p.look = msg.look ? cleanLook(msg.look) : null;
    // A jump (they used Home, say) snaps instead of gliding across the map.
    if (Math.hypot(msg.p.x - p.x, msg.p.y - p.y) > 900) { p.x = msg.p.x; p.y = msg.p.y; }
    p.tx = msg.p.x; p.ty = msg.p.y; p.vx = msg.p.vx; p.vy = msg.p.vy; p.facing = msg.p.f; p.effort = msg.p.e;
    p.seen = this.time; p.gone = 0;
  }
  peerLeft(id: string) { const p = this.peers.get(id); if (p) p.gone = this.time; }
  peerCount() { let n = 0; for (const p of this.peers.values()) if (p.look && !p.gone) n++; return n; }

  private updatePeers(dt: number) {
    const k = 1 - Math.exp(-8 * dt);
    for (const [id, p] of this.peers) {
      // Silent too long, or said goodbye: fade out, then forget.
      if (!p.gone && this.time - p.seen > 9) p.gone = this.time;
      if (p.gone && this.time - p.gone > 1) { this.peers.delete(id); continue; }
      // Keep going the way they were going between updates, and glide toward it.
      const coast = Math.min(this.time - p.seen, 0.6);
      const gx = p.tx + p.vx * coast, gy = p.ty + p.vy * coast;
      p.x += (gx - p.x) * k; p.y += (gy - p.y) * k;
    }
  }
  /** Swing the net (Space, or the net button). */
  swingNet() {
    if (this.mode !== 'play' || this.net.cooldown > 0) return;
    this.net = { swing: 0, cooldown: 0.65, caught: false };
  }
  /** Use the selected tool on the block in front (F). */
  useInFront() {
    const m = this.me;
    let dx: number = m.facing, dy = 0;
    if (this.keys.has('arrowdown') || this.keys.has('s') || this.stick.y > 0.6) { dx = 0; dy = 1; }
    else if (this.keys.has('arrowup') || this.keys.has('w') || this.stick.y < -0.6) { dx = 0; dy = -1; }
    const x = m.x + dx * (R + 18), y = m.y + dy * (R + 18);
    this.useAt(x, y);
  }

  // Spectate camera.
  fit() {
    this.userView = false;
    this.cam.z = Math.min(this.vw / this.world.w, this.vh / this.world.h) * 0.98 || 0.15;
    this.cam.x = this.world.w / 2; this.cam.y = this.world.h / 2;
    this.clampCam();
  }
  zoomBy(f: number, sx = this.vw / 2, sy = this.vh / 2) {
    this.userView = true;
    const before = this.toWorld(sx, sy);
    const minZ = Math.min(this.vw / this.world.w, this.vh / this.world.h) * 0.9;
    this.cam.z = Math.min(1.6, Math.max(minZ, this.cam.z * f));
    const after = this.toWorld(sx, sy);
    this.cam.x += before.x - after.x; this.cam.y += before.y - after.y;
    this.clampCam();
  }
  /** Teacher: look at a place on the map. */
  focusAt(x: number, y: number) {
    this.userView = true;
    this.cam.z = Math.max(this.cam.z, 0.7);
    this.cam.x = x; this.cam.y = y;
    this.clampCam();
  }

  // ── Loop ────────────────────────────────────────────────────────────────
  private start() {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    const frame = (now: number) => {
      if (!this.running) return;
      const dt = Math.min(0.05, (now - this.last) / 1000);
      this.last = now;
      // Hit-stop: when a blow lands, the world holds still for a few hundredths of a second.
      const held = this.stopT > 0;
      this.stopT = Math.max(0, this.stopT - dt);
      this.shakeT = Math.max(0, this.shakeT - dt);
      if (!held) this.time += dt;
      // Ask for the next frame first, so one failing frame can't stop the game for good.
      this.raf = requestAnimationFrame(frame);
      try { if (!held) this.update(dt); this.render(); } catch (err) {
        if (!this.failed) { this.failed = true; console.error('Ocean frame failed', err); }
      }
    };
    this.raf = requestAnimationFrame(frame);
  }
  private pause() { this.running = false; cancelAnimationFrame(this.raf); }

  private resize() {
    const r = this.canvas.getBoundingClientRect();
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.vw = Math.max(1, r.width); this.vh = Math.max(1, r.height);
    this.canvas.width = Math.round(this.vw * this.dpr);
    this.canvas.height = Math.round(this.vh * this.dpr);
    this.darkness.width = Math.ceil(this.canvas.width / 2);
    this.darkness.height = Math.ceil(this.canvas.height / 2);
    if (this.mode === 'play') this.cam.z = Math.min(1.25, Math.max(0.55, Math.min(this.vw, this.vh * 1.6) / 1300));
    else if (!this.userView) { this.fit(); return; }
    this.clampCam();
  }

  private clampCam() {
    const hw = this.vw / 2 / this.cam.z, hh = this.vh / 2 / this.cam.z;
    this.cam.x = hw * 2 >= this.world.w ? this.world.w / 2 : Math.min(this.world.w - hw, Math.max(hw, this.cam.x));
    this.cam.y = hh * 2 >= this.world.h + 80 ? this.world.h / 2 - 40 : Math.min(this.world.h - hh, Math.max(-80 + hh, this.cam.y));
  }
  private toWorld(sx: number, sy: number) {
    return { x: this.cam.x + (sx - this.vw / 2) / this.cam.z, y: this.cam.y + (sy - this.vh / 2) / this.cam.z };
  }

  // ── Input ───────────────────────────────────────────────────────────────
  private bindInput() {
    const c = this.canvas;
    const typing = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
    };
    const down = (e: KeyboardEvent) => {
      if (typing(e) || this.mode !== 'play' || this.paused) return;
      const k = e.key.toLowerCase();
      if (['arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'w', 'a', 's', 'd', ' '].includes(k)) e.preventDefault();
      if (k === ' ') { if (!e.repeat) { if (this.tool.kind === 'sword') this.swingSword(); else this.swingNet(); } return; }
      if ((k === 'e' || k === 'enter') && !e.repeat) { this.interact(); return; }
      if (k === 'f' && !e.repeat) { this.useInFront(); return; }
      this.keys.add(k);
    };
    const up = (e: KeyboardEvent) => this.keys.delete(e.key.toLowerCase());
    const blur = () => { this.keys.clear(); this.pointer = null; };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);

    const pos = (e: PointerEvent) => { const r = c.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const pdown = (e: PointerEvent) => {
      if (this.pointer) return;
      const p = pos(e);
      c.setPointerCapture(e.pointerId);
      this.pointer = { id: e.pointerId, x: p.x, y: p.y, sx: p.x, sy: p.y, t: performance.now(), moved: false, camX: this.cam.x, camY: this.cam.y };
    };
    const pmove = (e: PointerEvent) => {
      const p = pos(e);
      this.hover = p;
      const ptr = this.pointer;
      if (!ptr || ptr.id !== e.pointerId) return;
      ptr.x = p.x; ptr.y = p.y;
      if (Math.hypot(p.x - ptr.sx, p.y - ptr.sy) > 8) ptr.moved = true;
      if (this.mode === 'spectate' && ptr.moved) {
        this.userView = true;
        this.cam.x = ptr.camX - (p.x - ptr.sx) / this.cam.z;
        this.cam.y = ptr.camY - (p.y - ptr.sy) / this.cam.z;
        this.clampCam();
      }
    };
    const pup = (e: PointerEvent) => {
      const ptr = this.pointer;
      if (!ptr || ptr.id !== e.pointerId) return;
      this.pointer = null;
      const quick = performance.now() - ptr.t < 300;
      if (!ptr.moved && quick) this.tap(ptr.x, ptr.y);
    };
    const wheel = (e: WheelEvent) => {
      if (this.mode !== 'spectate') return;
      e.preventDefault();
      this.zoomBy(Math.exp(-e.deltaY * 0.0015), e.offsetX, e.offsetY);
    };
    const leave = () => { this.hover = null; };
    c.addEventListener('pointerdown', pdown);
    c.addEventListener('pointermove', pmove);
    c.addEventListener('pointerup', pup);
    c.addEventListener('pointercancel', pup);
    c.addEventListener('pointerleave', leave);
    c.addEventListener('wheel', wheel, { passive: false });
    this.cleanup.push(() => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
      c.removeEventListener('pointerdown', pdown);
      c.removeEventListener('pointermove', pmove);
      c.removeEventListener('pointerup', pup);
      c.removeEventListener('pointercancel', pup);
      c.removeEventListener('pointerleave', leave);
      c.removeEventListener('wheel', wheel);
    });
  }

  /** A quick tap: use the selected tool there, open what was tapped, or (teacher) pick a spot. */
  private tap(sx: number, sy: number) {
    const w = this.toWorld(sx, sy);
    if (this.mode === 'spectate') {
      const h = this.data.houses.find(h => { const size = HOUSES[h.kind].size, g = this.blocks.groundBelow(h.x, h.y - 20) ?? h.y; return Math.abs(w.x - h.x) < size * 0.6 && w.y > g - size - 30 && w.y < g + 10; });
      if (h) this.events.onHouseClick?.(h.id);
      return;
    }
    // Tapping the sign or spot you're next to opens it. Not a beacon while you hold a
    // pickaxe or a block, though: those clicks mine and build (beacons are walled in
    // by the blocks around them; the "Look at this habitat" button / E opens them).
    if (this.target && !(this.target.kind === 'beacon' && (this.tool.kind === 'pick' || this.tool.kind === 'block'))) {
      const at = this.targetPoint(this.target);
      if (at && Math.hypot(w.x - at.x, w.y - at.y) < 90 && this.tool.kind !== 'house') { this.interact(); return; }
    }
    if (this.tool.kind === 'net') { this.swingNet(); return; }
    if (this.tool.kind === 'sword') { this.swingSword(); return; }
    this.useAt(w.x, w.y);
  }

  /** Where a house would stand if placed at this point: on the ground below it, near you, in water. */
  private houseSpot(x: number, y: number): { x: number; y: number } | null {
    if (Math.hypot(x - this.me.x, y - this.me.y) > REACH * 2) return null;
    const ground = this.blocks.groundBelow(x, y);
    if (ground == null || ground - y > 260) return null;
    // The space the house stands in has to be open water.
    if (this.blocks.solidAtPoint(x, ground - 8) || this.blocks.solidAtPoint(x - 28, ground - 30) || this.blocks.solidAtPoint(x + 28, ground - 30)) return null;
    return { x, y: ground };
  }

  /** Mine, build or place a house at a point in the world. */
  private useAt(x: number, y: number) {
    const tool = this.tool;
    if (tool.kind === 'net') { this.swingNet(); return; }
    if (tool.kind === 'sword') { this.swingSword(); return; }
    if (tool.kind === 'house') {
      const spot = this.houseSpot(x, y);
      if (!spot) { this.events.onNotice?.('Houses go on the ground near you. Point at the sea floor (or a block) close by.'); return; }
      this.events.onPlaceHouse?.(tool.item, spot.x, spot.y);
      return;
    }
    const tx = Math.floor(x / B), ty = Math.floor(y / B);
    const cx = tx * B + B / 2, cy = ty * B + B / 2;
    if (Math.hypot(cx - this.me.x, cy - this.me.y) > REACH) { this.events.onNotice?.('Too far away. Swim closer.'); return; }
    const cur = this.blocks.at(tx, ty);
    if (tool.kind === 'pick') {
      if (cur === 0) return;
      const def = BLOCKS[cur];
      if (cur === 99) { this.events.onNotice?.('Bedrock can’t be mined.'); return; }
      if (this.gear.pickaxe <= 0) { this.events.onNotice?.('You need a pickaxe to mine. Buy one in the shop (C)!'); return; }
      if (def.drill && !this.gear.drill) { this.events.onNotice?.(`You need a Drill to mine ${def.name.toLowerCase()}. Craft one!`); return; }
      this.blocks.set(tx, ty, 0);
      this.editVer++;
      for (let i = 0; i < 8; i++) this.debris.push({ x: cx, y: cy, vx: (Math.random() - 0.5) * 220, vy: (Math.random() - 0.8) * 200, t: this.time, color: def.color });
      this.puffs.push({ x: cx, y: cy, t: this.time, color: def.color, r: 20 });
      this.shake(2, 0.08);
      this.events.onMine?.(tx, ty, def.item);
      return;
    }
    // Building.
    if (cur !== 0) return;
    if (this.gear.hammer <= 0) { this.events.onNotice?.('You need a hammer to build. Buy one in the shop (C)!'); return; }
    // Not on top of yourself.
    if (Math.abs(cx - this.me.x) < B / 2 + R && Math.abs(cy - this.me.y) < B / 2 + R) { this.events.onNotice?.('You’re in the way!'); return; }
    const def = blockByItem(tool.item);
    if (!def) return;
    this.blocks.set(tx, ty, def.id);
    this.editVer++;
    this.events.onBuild?.(tx, ty, def.item);
  }

  // ── Update ──────────────────────────────────────────────────────────────
  private update(dt: number) {
    this.updateDebris(dt);
    this.updatePeers(dt);
    if (this.mode !== 'play') {
      for (const [r, set] of this.remote) { if (this.time - set.at > 4) this.remote.delete(r); else this.followBeasts(set.list, dt); }
      return;
    }
    const m = this.me;
    let ix = 0, iy = 0;
    const k = this.keys;
    if (k.has('arrowleft') || k.has('a')) ix -= 1;
    if (k.has('arrowright') || k.has('d')) ix += 1;
    if (k.has('arrowup') || k.has('w')) iy -= 1;
    if (k.has('arrowdown') || k.has('s')) iy += 1;
    // The on-screen joystick.
    const sm = Math.hypot(this.stick.x, this.stick.y);
    if (sm > 0.15 && !this.paused) { ix = this.stick.x; iy = this.stick.y; }
    // Holding the pointer (or a finger) swims toward it.
    if (this.pointer && (this.pointer.moved || performance.now() - this.pointer.t > 160)) {
      const w = this.toWorld(this.pointer.x, this.pointer.y);
      const dx = w.x - m.x, dy = w.y - m.y, d = Math.hypot(dx, dy);
      if (d > 24) { ix = dx / d; iy = dy / d; }
    }
    // Knocked back by a bite: no swimming against it for a moment, and no speed limit.
    this.knock = Math.max(0, this.knock - dt);
    this.flash = Math.max(0, this.flash - dt);
    if (this.knock > 0) { ix = 0; iy = 0; }
    const len = Math.hypot(ix, iy);
    if (len > 0) { ix /= len; iy /= len; }
    m.vx += ix * ACCEL * dt; m.vy += iy * ACCEL * dt;
    const drag = Math.exp(-(this.knock > 0 ? 3.2 : DRAG) * dt);
    m.vx *= drag; m.vy *= drag;
    const sp = Math.hypot(m.vx, m.vy);
    if (sp > MAX_SPEED && this.knock <= 0) { m.vx *= MAX_SPEED / sp; m.vy *= MAX_SPEED / sp; }
    m.effort += ((len > 0 ? 1 : Math.min(1, sp / MAX_SPEED)) - m.effort) * Math.min(1, dt * 4);

    // Move one axis at a time so you slide along walls.
    const nx = m.x + m.vx * dt;
    if (!this.blocks.circleHits(nx, m.y, R) && !this.gateHits(nx, m.y, R)) m.x = nx; else m.vx *= -0.15;
    let ny = m.y + m.vy * dt;
    if (ny < 36) { ny = 36; m.vy = 0; }
    if (!this.blocks.circleHits(m.x, ny, R) && !this.gateHits(m.x, ny, R)) m.y = ny; else m.vy *= -0.15;
    // Bubbles stream behind a fish swimming fast.
    this.bubbleClock -= dt;
    if (Math.hypot(m.vx, m.vy) > 200 && this.bubbleClock <= 0) {
      this.bubbleClock = 0.05;
      this.bubbles.push({ x: m.x - m.facing * 26 + (Math.random() - 0.5) * 10, y: m.y + (Math.random() - 0.5) * 12, vx: -m.vx * 0.1, vy: -30 - Math.random() * 40, r: 2 + Math.random() * 4, t: this.time });
    }
    for (const b of this.bubbles) { b.x += b.vx * dt + Math.sin(this.time * 6 + b.r) * 0.4; b.y += b.vy * dt; b.vy -= 40 * dt; }
    this.bubbles = this.bubbles.filter(b => this.time - b.t < 1.4);
    this.puffs = this.puffs.filter(p => this.time - p.t < 0.6);
    // The Kraken's entrance swings the camera over for a moment.
    if (this.cine) { this.cine.t -= dt; if (this.cine.t <= 0) this.cine = null; }
    m.x = Math.min(this.world.w - R, Math.max(R, m.x));
    if (Math.abs(m.vx) > 18) m.facing = m.vx > 0 ? 1 : -1;   // a snap, never a squash
    // If a block was placed on you (by someone else), pop out.
    if (this.blocks.circleHits(m.x, m.y, R - 6)) this.goTo(m.x, m.y);

    // Camera follows.
    const f = 1 - Math.exp(-5 * dt);
    const focus = this.cine && this.cine.t > 0.6 ? this.cine : m;
    this.cam.x += (focus.x - this.cam.x) * (this.cine ? f * 0.5 : f);
    this.cam.y += (focus.y - this.cam.y) * (this.cine ? f * 0.5 : f);
    this.clampCam();

    this.updateNet(dt);
    this.updateWild(dt);
    this.updateBlade(dt);
    this.updateBeasts(dt);

    // Pearls.
    for (const p of this.pearls) {
      if (this.pearlsHad.has(p.id)) continue;
      if (Math.hypot(p.x - m.x, p.y - m.y) < R + 22) {
        this.pearlsHad.add(p.id);
        this.sparkles.push({ x: p.x, y: p.y, t: this.time });
        this.events.onPearl?.(p.id);
      }
    }

    // Where am I? (A few times a second is plenty.)
    this.areaCheck -= dt;
    if (this.areaCheck <= 0) {
      this.areaCheck = 0.25;
      this.world.bounds.forEach(([, b], i) => {
        if (this.revealed.has(i) || m.x < b[0] || m.x > b[2] || m.y < b[1] || m.y > b[3]) return;
        this.revealed.add(i); this.miniTick = 0;
        this.events.onReveal?.(i);
      });
      const a = this.world.areaAt(m.x, m.y).id;
      if (a !== this.area) {
        this.area = a;
        const isNew = !this.known.has(a);
        if (isNew) { this.known.add(a); this.miniTick = 0; }
        this.events.onArea?.(a, isNew);
      }
    }

    // Habitats nearby: ask now and then whether a fish moves in.
    this.visitCheck -= dt;
    if (this.visitCheck <= 0) {
      this.visitCheck = 2;
      for (const [tx, ty] of this.beaconList()) {
        if (Math.hypot(tx * B - m.x, ty * B - m.y) > 700) continue;
        const key = `${tx},${ty}`;
        if (this.time - (this.visits.get(key) ?? -1e9) < 45) continue;
        const st = this.habitat(tx, ty);
        if (!st.enclosed || !st.type) continue;
        this.visits.set(key, this.time);
        this.events.onHabitatVisit?.(tx, ty, st);
      }
    }

    const t = this.findTarget();
    if (JSON.stringify(t) !== JSON.stringify(this.target)) { this.target = t; this.events.onTarget?.(t); }
  }

  // ── Sea beasts ────────────────────────────────────────────────────────────
  // Shared by everyone in the same region of the map (live.ts REGION). One
  // classmate there "runs" them: spawns, hunts, takes hits. The rest follow what
  // they broadcast, and report bites and sword hits back. Whoever has the lowest
  // id among those broadcasting runs them; if they go quiet for 2.5 s, someone
  // else takes over with the beasts as they last were.

  private amRunning() {
    return !this.leader || this.leader === this.myId || this.time - this.leaderAt > 2.5;
  }

  /** Everyone swimming in my region right now, me first. */
  private regionPlayers(region: number): { id: string; x: number; y: number }[] {
    const out = [{ id: this.myId ?? 'me', x: this.me.x, y: this.me.y }];
    for (const p of this.peers.values()) if (!p.gone && regionOf(p.x) === region) out.push({ id: p.id, x: p.x, y: p.y });
    return out;
  }

  /** Beasts from whoever runs them in a region. */
  beastState(msg: BeastsMsg) {
    if (this.mode === 'spectate') {
      const prev = this.remote.get(msg.r);
      this.remote.set(msg.r, { at: this.time, list: this.mergeBeasts(prev?.list ?? [], msg) });
      return;
    }
    if (msg.r !== this.beastRegion) return;
    // Two of us running them: the lower id wins (I keep going if I'm lower and busy).
    const busy = this.amRunning() && this.broadcasting;
    if (busy && this.myId && this.myId < msg.from) return;
    if (!this.leader || this.time - this.leaderAt > 2.5 || msg.from <= this.leader) this.leader = msg.from;
    if (msg.from !== this.leader) return;
    this.leaderAt = this.time;
    this.broadcasting = false;
    this.beasts = this.mergeBeasts(this.beasts, msg);
    for (const d of msg.dead ?? []) {
      for (let i = 0; i < 12; i++) this.sparkles.push({ x: d.x + (Math.random() - 0.5) * 50, y: d.y + (Math.random() - 0.5) * 40, t: this.time - Math.random() * 0.3, color: '#ffffff' });
      if (d.by && d.by === this.myId) { sfxDefeat(); this.events.onBeastDefeated?.(d.k); }
      if (d.k === 'kraken') this.krakenBeaten({ home: { x: d.x, y: d.y } } as Beast);
    }
  }

  private mergeBeasts(old: Beast[], msg: BeastsMsg): Beast[] {
    const byId = new Map(old.map(b => [b.id, b]));
    return msg.list.map(w => {
      let b = byId.get(w.id);
      if (!b) b = { id: w.id, kind: w.k, x: w.x, y: w.y, vx: 0, vy: 0, hp: w.hp, facing: w.f, cool: 0, hurt: 0, home: { x: w.x, y: w.y }, lunge: 0, turn: 0, seed: w.s };
      if (Math.hypot(w.x - b.x, w.y - b.y) > 300) { b.x = w.x; b.y = w.y; }
      if (w.hp < b.hp) b.hurt = Math.max(b.hurt, 0.35);
      b.tx = w.x; b.ty = w.y; b.at = this.time;
      b.vx = w.vx; b.vy = w.vy; b.hp = w.hp; b.facing = w.f; b.cool = w.c; b.lunge = w.l; if (w.m) b.max = w.m;
      return b;
    });
  }

  /** A follower says a beast bit them or they hit it. Only the one running them acts on it. */
  beastEvent(e: BeastEvent) {
    if (this.mode !== 'play' || !this.amRunning()) return;
    const b = this.beasts.find(x => x.id === e.id);
    if (!b) return;
    if (e.t === 'bite') { b.cool = Math.max(b.cool, 1.6); b.lunge = 0; return; }
    this.hitBeast(b, e.dmg ?? 1, e.fx ?? 1, e.by);
  }

  /** Glide followed beasts toward where they were last said to be. */
  private followBeasts(list: Beast[], dt: number) {
    const k = 1 - Math.exp(-6 * dt);
    for (const b of list) {
      b.hurt = Math.max(0, b.hurt - dt);
      b.cool = Math.max(0, b.cool - dt);
      b.lunge = Math.max(0, b.lunge - dt);
      if (b.tx == null || b.at == null) continue;
      const coast = Math.min(this.time - b.at, 0.7);
      const gx = b.tx + b.vx * coast, gy = (b.ty ?? b.y) + b.vy * coast;
      b.x += (gx - b.x) * k; b.y += (gy - b.y) * k;
    }
  }

  /** Sea beasts: appear in the lower levels around players, hunt or drift, and bite. */
  private updateBeasts(dt: number) {
    const m = this.me;
    this.invuln = Math.max(0, this.invuln - dt);
    this.sinceHit += dt;
    // A life comes back after 30 s without a bite.
    if (this.lives < 3 && this.sinceHit > 30) { this.lives++; this.sinceHit = 0; this.events.onLives?.(this.lives, null); }
    for (const [id, c] of this.biteCool) { if (c - dt <= 0) this.biteCool.delete(id); else this.biteCool.set(id, c - dt); }

    // Swam into another region: its beasts are someone else's business until we hear.
    const region = regionOf(m.x);
    if (region !== this.beastRegion) { this.beastRegion = region; this.beasts = []; this.leader = null; this.broadcasting = false; }

    if (this.amRunning()) this.runBeasts(dt, region);
    else this.followBeasts(this.beasts, dt);
    for (const b of this.beasts) {
      if (!this.appear.has(b.id)) {
        this.appear.set(b.id, this.time);
        for (let i = 0; i < 10; i++) this.sparkles.push({ x: b.x + (Math.random() - 0.5) * 60, y: b.y + (Math.random() - 0.5) * 50, t: this.time - Math.random() * 0.2, color: 'rgba(220,240,255,0.9)' });
      }
      if (b.kind === 'kraken') {
        const ti = this.krakenTemple(b);
        if (ti >= 0) this.krakenMemo.set(ti, { hp: b.hp, max: b.max ?? BEASTS.kraken.hp, at: this.time });
      }
    }
    if (this.appear.size > 200) for (const id of this.appear.keys()) if (!this.beasts.some(b => b.id === id)) this.appear.delete(id);
    for (const b of this.beasts) {
      if (b.kind !== 'kraken' || this.bossSeen.has(b.id) || Math.hypot(b.x - m.x, b.y - m.y) > 1100) continue;
      this.bossSeen.add(b.id);
      this.cine = { x: b.x, y: b.y, t: 2 };
      this.shake(10, 1.2);
      this.events.onBoss?.(b.kind);
    }

    // Bites (and stings), checked by each player for themselves.
    if (this.paused || this.invuln > 0) return;
    for (const b of this.beasts) {
      const def = BEASTS[b.kind];
      if (b.cool > 0 || this.biteCool.has(b.id) || this.time - (this.appear.get(b.id) ?? 0) < 1.2) continue;
      const dx = m.x - b.x, dy = m.y - b.y, d = Math.hypot(dx, dy) || 1;
      if (d >= R + def.r * 0.85) continue;
      this.biteCool.set(b.id, 1.6);
      if (this.amRunning()) { b.cool = 1.6; if (b.kind === 'eel') b.lunge = 0; }
      else if (this.myId) this.events.onBeastEvent?.({ t: 'bite', id: b.id, by: this.myId });
      this.hurtMe(b, dx / d, dy / d);
      break;
    }
  }

  /** Running the beasts of my region: spawn, hunt, move, and share them. */
  private runBeasts(dt: number, region: number) {
    const players = this.regionPlayers(region);
    const deep = players.filter(p => p.y > BEAST_LINE - 200);
    const x0 = region * REGION, x1 = x0 + REGION;
    // Leave when far from everyone, or when everyone's gone up out of their waters.
    this.beasts = this.beasts.filter(b => players.some(p => Math.hypot(b.x - p.x, b.y - p.y) < 1700 && !(p.y < BEAST_LINE - 300 && Math.hypot(b.x - p.x, b.y - p.y) > 500)));
    this.beastTimer -= dt;
    if (this.beastTimer <= 0) {
      this.beastTimer = 2.5;
      const cap = Math.min(8, 3 + deep.length);
      if (this.beasts.length < cap && deep.length) {
        const near = deep[Math.floor(Math.random() * deep.length)];
        // In a temple, beasts come out of its rooms (the rock between is solid).
        const rooms = this.world.temples.flatMap(t => (Math.abs(t.box.x + t.box.w / 2 - near.x) < t.box.w ? [...t.rooms, t.abyss] : []))
          .filter(q => this.world.areaAt(near.x, near.y).id === 'temple' && Math.hypot(q.x + q.w / 2 - near.x, q.y + q.h / 2 - near.y) < 1800);
        for (let tries = 0; tries < 16; tries++) {
          const a = Math.random() * Math.PI * 2, d = 1150 + Math.random() * 350;
          const q = rooms.length ? rooms[Math.floor(Math.random() * rooms.length)] : null;
          const x = q ? q.x + 50 + Math.random() * (q.w - 100) : near.x + Math.cos(a) * d;
          const y = q ? q.y + 50 + Math.random() * (q.h - 100) : near.y + Math.sin(a) * d;
          // Off everyone's screen, so nothing appears out of nowhere: they swim in from the edge.
          if (players.some(p => Math.abs(x - p.x) < SPAWN_CLEAR.x && Math.abs(y - p.y) < SPAWN_CLEAR.y)) continue;
          if (x < Math.max(80, x0) || x > Math.min(this.world.w - 80, x1) || y < BEAST_LINE || y > this.world.h - 60) continue;
          if (this.blocks.circleHits(x, y, 40)) continue;
          const kind = beastFor(this.world.areaAt(x, y).id, y, Math.random);
          if (!kind || (BEASTS[kind].unique && this.beasts.some(b => b.kind === kind))) continue;
          if (this.blocks.circleHits(x, y, BEASTS[kind].r)) continue;
          this.beasts.push({ id: `${(this.myId ?? 'me').slice(0, 6)}${++this.beastId}`, kind, x, y, vx: 0, vy: 0, hp: BEASTS[kind].hp, facing: 1, cool: 0, hurt: 0, home: { x, y }, lunge: 0, turn: 0, seed: Math.random() * 100 });
          break;
        }
      }
    }

    // Someone swam into the Abyss: the Kraken rises from the far side.
    for (const t of this.world.temples) {
      const ab = t.abyss;
      const inside = players.find(p => p.x > ab.x && p.x < ab.x + ab.w && p.y > ab.y - 40 && p.y < ab.y + ab.h);
      if (!inside || this.beasts.some(b => b.kind === 'kraken') || ab.x < x0 - ab.w || ab.x > x1) continue;
      const kx = inside.x < ab.x + ab.w / 2 ? ab.x + ab.w - 140 : ab.x + 140;
      // Tougher the more of you there are: the class has to fight it together.
      const fighters = players.filter(p => p.x > ab.x - 200 && p.x < ab.x + ab.w + 200 && p.y > ab.y - 800 && p.y < ab.y + ab.h).length;
      // Beaten a little while ago: it's resting. Hurt in a fight that's still going: it comes back as hurt as it was.
      const ti = this.world.temples.indexOf(t), memo = this.krakenMemo.get(ti);
      if (memo?.beaten && this.time - memo.beaten < KRAKEN_REST) continue;
      const fresh = BEASTS.kraken.hp + 25 * Math.max(0, fighters - 1);
      const going = memo && !memo.beaten && this.time - memo.at < KRAKEN_MEMORY;
      const max = going ? Math.max(memo.max, fresh) : fresh;
      const hp = going ? Math.min(memo.hp + (max - memo.max), max) : fresh;
      this.beasts.push({ id: `${(this.myId ?? 'me').slice(0, 6)}${++this.beastId}`, kind: 'kraken', max, x: Math.max(x0 + 100, Math.min(x1 - 100, kx)), y: ab.y + ab.h / 2, vx: 0, vy: 0, hp, facing: kx > inside.x ? -1 : 1, cool: 1.5, hurt: 0, home: { x: kx, y: ab.y + ab.h / 2 }, lunge: 0, turn: 0, seed: Math.random() * 100 });
    }

    for (const b of this.beasts) {
      const def = BEASTS[b.kind];
      b.cool = Math.max(0, b.cool - dt);
      b.hurt = Math.max(0, b.hurt - dt);
      b.turn -= dt;
      // Hunt whoever's closest (and down in their waters).
      let prey = deep[0] ?? players[0], pd = Infinity;
      for (const p of deep) { const d = Math.hypot(p.x - b.x, p.y - b.y); if (d < pd) { pd = d; prey = p; } }
      const dx = prey.x - b.x, dy = prey.y - b.y, d = Math.hypot(dx, dy) || 1;
      const sees = def.aggro > 0 && d < def.aggro && b.cool <= 0 && prey.y > BEAST_LINE - 150 && !(prey.id === (this.myId ?? 'me') && this.paused);
      let tx = b.vx, ty = b.vy, ease = 2;
      if (b.kind === 'eel') {
        // Lurks by its hole; lunges when you come close, then slinks back.
        b.lunge = Math.max(0, b.lunge - dt);
        if (sees && b.lunge <= 0 && Math.hypot(prey.x - b.home.x, prey.y - b.home.y) < def.aggro + 60) { b.lunge = 0.55; b.vx = (dx / d) * def.chase; b.vy = (dy / d) * def.chase; }
        if (b.lunge > 0) { tx = b.vx; ty = b.vy; ease = 0; }
        else { const hx = b.home.x - b.x, hy = b.home.y - b.y; tx = hx * 1.5 + Math.sin(this.time + b.seed) * 20; ty = hy * 1.5; ease = 3; }
      } else if (sees) {
        tx = (dx / d) * def.chase; ty = (dy / d) * def.chase; ease = 2.5;
      } else if (b.turn <= 0) {
        b.turn = 2 + Math.random() * 3;
        // Hunters patrol toward players when they're far off; otherwise they roam.
        const a = def.aggro > 0 && d > def.aggro * 0.7 ? Math.atan2(dy, dx) + (Math.random() - 0.5) * 1.8 : Math.random() * Math.PI * 2;
        b.vx = Math.cos(a) * def.wander; b.vy = Math.sin(a) * def.wander * (b.kind === 'jelly' ? 1 : 0.4);
        tx = b.vx; ty = b.vy;
      }
      // Backing off after a bite.
      if (b.cool > 0.5 && b.kind !== 'eel') { tx = (-dx / d) * def.chase * 0.6; ty = (-dy / d) * def.chase * 0.6; }
      if (ease > 0) { const f = 1 - Math.exp(-ease * dt); b.vx += (tx - b.vx) * f; b.vy += (ty - b.vy) * f; }
      if (b.kind === 'jelly') b.vy += Math.sin(this.time * 1.2 + b.seed) * 6 * dt;
      if (def.walks) b.vy = Math.min(b.vy + 600 * dt, 260);   // crabs stay on the floor
      const nx = b.x + b.vx * dt, ny = b.y + b.vy * dt;
      // They stay in this region (so everyone who can see them hears about them).
      if (nx < x0 + 40 || nx > x1 - 40 || this.blocks.circleHits(nx, b.y, def.r * 0.8)) b.vx = -b.vx * 0.5; else b.x = nx;
      if (ny < BEAST_LINE || this.blocks.circleHits(b.x, ny, def.r * 0.8)) b.vy = -b.vy * 0.5; else b.y = ny;
      if (Math.abs(b.vx) > 12) b.facing = b.vx > 0 ? 1 : -1;   // snap, like everyone else
    }

    // Share them twice a second while there are any (or someone's down here), and once more when they're gone.
    this.beastSend -= dt;
    const worth = this.beasts.length > 0 || deep.length > 0;
    if (this.myId && this.beastSend <= 0 && (worth || this.broadcasting)) {
      this.beastSend = 0.5;
      this.events.onBeastSync?.({ r: region, from: this.myId, list: this.beasts.map(toWire), dead: this.deadQueue.length ? this.deadQueue : undefined });
      this.deadQueue = [];
      this.broadcasting = worth;
    }
  }

  /** A sword or trident hit a beast I'm running. */
  private hitBeast(b: Beast, dmg: number, fx: 1 | -1, by: string | null) {
    if (by === this.myId) { sfxHit(); this.shake(4, 0.12, 0.04); }
    this.puffs.push({ x: b.x, y: b.y, t: this.time, color: 'rgba(255,255,255,0.8)', r: BEASTS[b.kind].r });
    b.hp -= dmg; b.hurt = 0.35; b.cool = Math.max(b.cool, 0.9); b.lunge = 0;
    b.vx = fx * 420; b.vy = 0;
    if (b.hp > 0) {
      // Remember the wound at once, not just next frame, so nothing can heal it.
      const ti = b.kind === 'kraken' ? this.krakenTemple(b) : -1;
      if (ti >= 0) this.krakenMemo.set(ti, { hp: b.hp, max: b.max ?? BEASTS.kraken.hp, at: this.time });
      return;
    }
    this.beasts = this.beasts.filter(x => x !== b);
    for (let i = 0; i < 12; i++) this.sparkles.push({ x: b.x + (Math.random() - 0.5) * 50, y: b.y + (Math.random() - 0.5) * 40, t: this.time - Math.random() * 0.3, color: '#ffffff' });
    this.deadQueue.push({ id: b.id, x: Math.round(b.x), y: Math.round(b.y), by, k: b.kind });
    this.events.onBeastDied?.(b.kind);
    if (b.kind === 'kraken') { this.shake(22, 0.8, 0.12); this.burst(b.x, b.y, '#ffd23d', 40); this.krakenBeaten(b); }
    if (by === this.myId || (!by && !this.myId)) { sfxDefeat(); this.events.onBeastDefeated?.(b.kind); }
  }

  /** Which temple's Abyss a Kraken belongs to: the nearest one to where it rose (or died), or -1 with no temples. */
  private krakenTemple(b: Beast): number {
    let best = -1, bd = Infinity;
    this.world.temples.forEach((t, i) => {
      const d = Math.hypot(b.home.x - (t.abyss.x + t.abyss.w / 2), b.home.y - (t.abyss.y + t.abyss.h / 2));
      if (d < bd) { bd = d; best = i; }
    });
    return best;
  }
  private krakenBeaten(b: Beast) {
    const ti = this.krakenTemple(b);
    if (ti >= 0) this.krakenMemo.set(ti, { hp: 0, max: b.max ?? BEASTS.kraken.hp, at: this.time, beaten: this.time });
  }

  private hurtMe(b: Beast, nx: number, ny: number) {
    const m = this.me;
    this.lives--;
    this.invuln = 2.2;
    this.sinceHit = 0;
    // Knocked away from the beast, flashing red.
    const kick = b.kind === 'kraken' ? 900 : b.kind === 'jelly' ? 520 : 700;
    m.vx = nx * kick; m.vy = ny * kick;
    this.knock = 0.4;
    this.flash = 0.45;
    this.shake(b.kind === 'kraken' ? 18 : 11, 0.35, 0.06);
    if (this.lives > 0) sfxHurt(); else sfxFaint();
    for (let i = 0; i < 8; i++) this.debris.push({ x: m.x, y: m.y, vx: (Math.random() - 0.5) * 260, vy: (Math.random() - 0.5) * 260, t: this.time, color: '#ff5a6e' });
    if (this.lives > 0) { this.events.onLives?.(this.lives, b.kind); return; }
    // Out of lives: back to the start of the ocean. (The beasts stay, for whoever's still down there.)
    this.lives = 3;
    this.invuln = 3;
    this.goTo(this.world.spawn.x, this.world.spawn.y);
    m.vx = 0; m.vy = 0;
    this.events.onLives?.(3, null);
    this.events.onFaint?.(b.kind);
  }

  private updateBlade(dt: number) {
    const s = this.blade;
    s.cooldown = Math.max(0, s.cooldown - dt);
    if (s.swing < 0) return;
    s.swing += dt / 0.3;
    if (!s.hit && s.swing >= 0.4) {
      s.hit = true;
      const trident = this.gear.trident > 0;
      const reach = trident ? 110 : 85, dmg = trident ? 2 : 1;
      const m = this.me, hx = m.x + m.facing * 40, hy = m.y;
      let landed = false;
      for (const b of [...this.beasts]) {
        const r = BEASTS[b.kind].r;
        if (Math.hypot(b.x - hx, b.y - hy) > reach + r * 0.6 || (b.x - m.x) * m.facing < -r) continue;
        landed = true;
        if (this.amRunning()) this.hitBeast(b, dmg, m.facing, this.myId);
        else {
          // Show it here at once; the one running the beasts decides what happens.
          b.hurt = 0.35;
          sfxHit();
          this.shake(4, 0.12, 0.04);
          this.puffs.push({ x: b.x, y: b.y, t: this.time, color: 'rgba(255,255,255,0.8)', r: BEASTS[b.kind].r });
          if (this.myId) this.events.onBeastEvent?.({ t: 'hit', id: b.id, by: this.myId, dmg, fx: m.facing });
        }
      }
      if (landed) {
        const w = trident ? 'trident' : 'sword';
        this.gear[w] = Math.max(0, this.gear[w] - 1);
        this.events.onWeaponHit?.(w);
      }
    }
    if (s.swing >= 1) s.swing = -1;
  }

  private updateNet(dt: number) {
    const n = this.net;
    n.cooldown = Math.max(0, n.cooldown - dt);
    if (n.swing < 0) return;
    n.swing += dt / 0.35;
    // The catch happens mid-swing, in front of you.
    if (!n.caught && n.swing >= 0.45) {
      n.caught = true;
      const reach = this.gear.goldenNet ? 78 : 56;
      const hx = this.me.x + this.me.facing * 48, hy = this.me.y;
      const hit = this.wild
        .map(f => ({ f, d: Math.hypot(f.x - hx, f.y - hy) }))
        .filter(({ f, d }) => d < reach + speciesById(f.species).size * 18)
        .sort((a, b) => a.d - b.d)[0];
      if (hit && this.bucket >= BUCKET_SIZE) {
        this.events.onNotice?.(`Your bucket is full (${BUCKET_SIZE} fish). Move them into a fish house (B) to catch more.`);
      } else if (hit) {
        this.bucket++;
        this.wild = this.wild.filter(f => f !== hit.f);
        this.sparkles.push({ x: hit.f.x, y: hit.f.y, t: this.time, color: '#9ff7c8' });
        this.events.onCatch?.(hit.f.species);
      }
    }
    if (n.swing >= 1) n.swing = -1;
  }

  /** Wild fish: a handful around the player, coming and going. */
  private updateWild(dt: number) {
    const m = this.me;
    const hw = this.vw / this.cam.z, hh = this.vh / this.cam.z;
    // Leave when far away.
    this.wild = this.wild.filter(f => Math.abs(f.x - m.x) < hw * 1.6 && Math.abs(f.y - m.y) < hh * 1.8);
    this.spawnTimer -= dt;
    if (this.spawnTimer <= 0 && this.wild.length < 9) {
      this.spawnTimer = 0.6;
      for (let tries = 0; tries < 12; tries++) {
        const x = m.x + (Math.random() - 0.5) * hw * 2.2, y = Math.max(80, m.y + (Math.random() - 0.5) * hh * 2.2);
        if (x < 60 || x > this.world.w - 60 || y > this.world.h - 80) continue;
        if (Math.hypot(x - m.x, y - m.y) < 380) continue;            // not right on top of you
        if (this.blocks.circleHits(x, y, 30)) continue;
        const area = this.world.areaAt(x, y).id;
        const species = this.pickSpecies(area);
        if (!species) continue;
        const sp = speciesById(species);
        let fy = y;
        if (sp.habitat === 'bottom') {
          const g = this.blocks.groundBelow(x, y);
          if (g == null || g - y > 300) continue;
          fy = g - 20;
        }
        this.wild.push({ id: ++this.wildId, species, x, y: fy, vx: (Math.random() - 0.5) * 60, vy: 0, turn: Math.random() * 3, dash: 0, born: this.time });
        // Something rare: a shimmer, a sound, and a heads-up (not too often).
        if ((sp.rarity === 'epic' || sp.rarity === 'legendary' || sp.rarity === 'mythic') && this.time - this.rareSeen > 20) {
          this.rareSeen = this.time;
          sfxRare();
          this.events.onRareFish?.(species);
        }
        break;
      }
    }
    for (const f of this.wild) {
      const sp = speciesById(f.species);
      const d = Math.hypot(f.x - m.x, f.y - m.y);
      // Spooked by a fast swimmer close by: a dash away.
      const shy = sp.rarity === 'epic' || sp.rarity === 'legendary' || sp.rarity === 'mythic';
      if (shy && d < 240 && f.dash <= 0 && Math.random() < dt * 4) {
        // Rare fish are skittish: they bolt as soon as you get close.
        f.dash = 0.9;
        const a = Math.atan2(f.y - m.y, f.x - m.x);
        f.vx = Math.cos(a) * 380; f.vy = sp.habitat === 'bottom' ? 0 : Math.sin(a) * 260;
      } else if (d < 150 && Math.hypot(m.vx, m.vy) > 180 && f.dash <= 0 && Math.random() < dt * 2.5) {
        f.dash = 0.7;
        const a = Math.atan2(f.y - m.y, f.x - m.x);
        f.vx = Math.cos(a) * 260; f.vy = sp.habitat === 'bottom' ? 0 : Math.sin(a) * 200;
      }
      f.dash -= dt;
      f.turn -= dt;
      if (f.turn <= 0 && f.dash <= 0) {
        f.turn = 1.5 + Math.random() * 3;
        const speed = sp.habitat === 'bottom' ? 30 : sp.habitat === 'float' ? 22 : 55;
        f.vx = (Math.random() - 0.5) * 2 * speed;
        f.vy = sp.habitat === 'bottom' ? 0 : (Math.random() - 0.5) * speed * 0.6;
      }
      const k = Math.exp(-(f.dash > 0 ? 1.2 : 0.3) * dt);
      if (f.dash > 0) { f.vx *= k; f.vy *= k; }
      const nx = f.x + f.vx * dt, ny = f.y + f.vy * dt;
      if (this.blocks.circleHits(nx, f.y, 18)) f.vx = -f.vx; else f.x = nx;
      if (sp.habitat === 'bottom') {
        const g = this.blocks.groundBelow(f.x, f.y - 10);
        if (g != null) f.y += (g - 20 - f.y) * Math.min(1, dt * 6);
      } else if (ny < 60 || this.blocks.circleHits(f.x, ny, 18)) f.vy = -f.vy; else f.y = ny;
    }
  }

  private pickSpecies(area: AreaId): string | null {
    const where = CAVE_AREAS.has(area) ? 'caves' : area === 'cabin' ? 'wreck' : area;
    // The golden net brings the rarer fish out more often.
    const boost = (r: string) => (this.gear.goldenNet && r !== 'common' && r !== 'uncommon' ? 2 : 1);
    // At night, some deep-sea fish rise toward the surface.
    const night = nightness() > 0.5;
    const entries = SPECIES.filter(sp => (!sp.nightOnly || night) && (sp.areas.includes(where) || (night && sp.night?.includes(where))))
      .map(sp => [sp.id, RARITY[sp.rarity].weight * boost(sp.rarity)] as const);
    const total = entries.reduce((a, [, w]) => a + w, 0);
    let r = Math.random() * total;
    for (const [s, w] of entries) if ((r -= w) <= 0) return s;
    return entries[0]?.[0] ?? null;
  }


  private updateDebris(dt: number) {
    this.debris = this.debris.filter(p => this.time - p.t < 0.8);
    for (const p of this.debris) { p.vy += 260 * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vx *= 0.96; }
  }

  private findTarget(): Target {
    const m = this.me;
    let best: Target = null, bestD = 150;
    for (const s of this.signs) {
      const d = Math.hypot(s.x - m.x, s.y - 60 - m.y);
      if (d < bestD) { bestD = d; best = { kind: 'sign', i: s.i }; }
    }
    for (const h of this.data.houses) {
      const p = this.doorOf(h);
      // Houses are big: the door counts from a little further away.
      const d = Math.hypot(p.x - m.x, p.y - m.y) - HOUSES[h.kind].size * 0.5;
      if (d < bestD) { bestD = d; best = { kind: 'house', id: h.id }; }
    }
    for (const c of this.chests) {
      if (this.opened.has(c.id)) continue;
      const d = Math.hypot(c.x - m.x, c.y - 24 - m.y) - 20;
      if (d < bestD) { bestD = d; best = { kind: 'chest', id: c.id, tier: c.tier }; }
    }
    for (const [tx, ty] of this.beaconList()) {
      const d = Math.hypot(tx * B + B / 2 - m.x, ty * B + B / 2 - m.y) - 10;
      if (d < bestD) { bestD = d; best = { kind: 'beacon', tx, ty }; }
    }
    for (const gt of this.world.gates) {
      if (this.gatesOpen.has(`gate${gt.i}`)) continue;
      const d = Math.hypot(gt.x + gt.w / 2 - m.x, gt.y + gt.h / 2 - m.y) - 70;
      if (d < bestD) { bestD = d; best = { kind: 'gate', i: gt.i }; }
    }
    for (const n of this.npcs) {
      const d = Math.hypot(n.x - m.x, n.y - 40 - m.y) - 40;
      if (d < bestD) { bestD = d; best = { kind: 'npc', id: n.id }; }
    }
    return best;
  }

  private targetPoint(t: Target): { x: number; y: number } | null {
    if (!t) return null;
    if (t.kind === 'house') { const h = this.data.houses.find(x => x.id === t.id); return h ? this.doorOf(h) : null; }
    if (t.kind === 'chest') { const c = this.chests.find(x => x.id === t.id); return c ? { x: c.x, y: c.y - 24 } : null; }
    if (t.kind === 'beacon') return { x: t.tx * B + B / 2, y: t.ty * B + B / 2 };
    if (t.kind === 'gate') { const gt = this.world.gates[t.i]; return { x: gt.x + gt.w / 2, y: gt.y + gt.h / 2 }; }
    if (t.kind === 'npc') { const n = this.npcs.find(x => x.id === t.id); return n ? { x: n.x, y: n.y - 40 } : null; }
    const s = this.signs.find(x => x.i === t.i)!;
    return { x: s.x, y: s.y - 60 };
  }

  /** A house's doorway in the world, on whatever ground is under it now. */
  private doorOf(h: OceanHouse): { x: number; y: number } {
    const ground = this.blocks.groundBelow(h.x, h.y - 20) ?? h.y;
    const d = houseDoor(h.kind);
    return { x: h.x + d.dx, y: ground + d.dy };
  }

  // ── Drawing ─────────────────────────────────────────────────────────────
  private render() {
    const g = this.ctx, dpr = this.dpr, z = this.cam.z;
    const W = this.canvas.width, H = this.canvas.height;
    const t = this.time;
    g.setTransform(1, 0, 0, 1, 0, 0);

    const syOf = (wy: number) => ((wy - this.cam.y) * z + this.vh / 2) * dpr;
    const water = g.createLinearGradient(0, syOf(0), 0, syOf(this.world.h));
    water.addColorStop(0, '#86d6ee');
    water.addColorStop(0.3, '#3a96bf');
    water.addColorStop(0.62, '#185a7c');
    water.addColorStop(1, '#06213a');
    g.fillStyle = water;
    g.fillRect(0, 0, W, H);
    const sTop = syOf(0);
    if (sTop > 0) { g.fillStyle = '#e4f7ff'; g.fillRect(0, 0, W, sTop); }

    const view: View = { x0: this.cam.x - this.vw / 2 / z, x1: this.cam.x + this.vw / 2 / z, y0: this.cam.y - this.vh / 2 / z, y1: this.cam.y + this.vh / 2 / z };

    // Light rays near the top.
    const night = nightness();
    const rayAlpha = Math.max(0, 1 - this.cam.y / 1400) * 0.14 * (1 - night);
    if (rayAlpha > 0.005) {
      g.save();
      g.globalCompositeOperation = 'lighter';
      for (let i = 0; i < 6; i++) {
        const wx = Math.floor(view.x0 / 600) * 600 + i * 600 + 200;
        const sx = ((wx - this.cam.x) * z * 0.85 + this.vw / 2) * dpr;
        const sway = Math.sin(t * 0.3 + i) * 30 * dpr;
        const ray = g.createLinearGradient(0, sTop, 0, syOf(1300));
        ray.addColorStop(0, `rgba(255,255,255,${rayAlpha})`);
        ray.addColorStop(1, 'rgba(255,255,255,0)');
        g.fillStyle = ray;
        g.beginPath();
        g.moveTo(sx + sway, sTop); g.lineTo(sx + 90 * dpr * z + sway, sTop);
        g.lineTo(sx + 260 * dpr * z, syOf(1300)); g.lineTo(sx + 100 * dpr * z, syOf(1300)); g.closePath(); g.fill();
      }
      g.restore();
    }

    // Distant hills, drifting slower than the camera.
    g.save();
    const par = 0.55;
    g.setTransform(z * dpr, 0, 0, z * dpr, (-this.cam.x * par * z + this.vw / 2) * dpr, (-this.cam.y * z + this.vh / 2) * dpr);
    g.fillStyle = 'rgba(20,70,100,0.28)';
    g.beginPath();
    g.moveTo(HILLS[0][0], this.world.h);
    for (const [x, y] of HILLS) g.lineTo(x, y);
    g.lineTo(HILLS[HILLS.length - 1][0], this.world.h);
    g.closePath(); g.fill();
    g.restore();

    const shk = this.shakeT > 0 ? this.shakeAmp * Math.min(1, this.shakeT / 0.2) : 0;
    const ox = (Math.random() - 0.5) * 2 * shk, oy = (Math.random() - 0.5) * 2 * shk;
    g.setTransform(z * dpr, 0, 0, z * dpr, (-this.cam.x * z + this.vw / 2 + ox) * dpr, (-this.cam.y * z + this.vh / 2 + oy) * dpr);
    const lights: [number, number, number, string][] = [];

    this.drawArch(g, view);
    this.drawBlocks(g, view, lights);
    this.drawDecorations(g, view, lights);
    for (const p of this.pearls) if (!this.pearlsHad.has(p.id) && this.inView(view, p.x, p.y, 40)) drawPearl(g, p.x, p.y, t, lights);
    for (const s of this.signs) if (this.inView(view, s.x, s.y, 140)) drawSign(g, s, t, this.target?.kind === 'sign' && this.target.i === s.i);
    this.drawGates(g, view, lights);
    for (const n of this.npcs) if (this.inView(view, n.x, n.y, 160)) drawNpc(g, n.id, n.x, n.y, t, this.mode === 'play' && !!nextQuest(n.id, this.questsDone), this.target?.kind === 'npc' && this.target.id === n.id);
    this.drawHouses(g, view);
    this.drawChests(g, view, lights);
    this.drawHabitats(g, view, lights);
    this.drawWild(g, view, lights);
    const beasts = this.mode === 'play' ? this.beasts : [...this.remote.values()].flatMap(s => s.list);
    for (const b of beasts) {
      if (!this.inView(view, b.x, b.y, 120)) continue;
      // Hit: a red flash that fades.
      const fade = Math.min(1, (this.time - (this.appear.get(b.id) ?? -9)) / 0.8);
      if (fade < 1) g.globalAlpha = fade;
      if (b.hurt > 0) this.drawFlashed(g, b.x, b.y, BEASTS[b.kind].r * 2.6 + 40, Math.min(1, b.hurt / 0.35) * 0.75, gg => drawBeast(gg, b, t, lights));
      else drawBeast(g, b, t, lights);
      if (fade < 1) g.globalAlpha = 1;
    }
    this.drawPeers(g, view);
    if (this.mode === 'play' && this.look) {
      const m = this.me;
      // Blink while you can't be bitten again.
      g.save();
      if (this.invuln > 0 && this.flash <= 0 && Math.floor(this.invuln * 10) % 2 === 0) g.globalAlpha = 0.35;
      const look = this.look;
      const avatar = (gg: CanvasRenderingContext2D) => drawAvatar(gg, look, { x: m.x, y: m.y, size: 64, facing: m.facing, effort: m.effort, tilt: Math.max(-0.5, Math.min(0.5, Math.atan2(m.vy, Math.abs(m.vx) + 60))), t });
      if (this.flash > 0) this.drawFlashed(g, m.x, m.y, 80, Math.min(1, this.flash / 0.45) * 0.8, avatar);
      else avatar(g);
      g.restore();
      if (this.tool.kind === 'sword' || this.blade.swing >= 0) {
        if (this.gear.sword + this.gear.trident > 0 || this.blade.swing >= 0) drawWeapon(g, m.x, m.y, m.facing, this.blade.swing, this.gear.trident > 0);
      } else this.drawNet(g);
      // The deeper you go, the less your own glow reaches.
      const dim = Math.max(0.5, Math.min(1, 1 - (m.y - 1800) / 9000));
      lights.push([m.x, m.y, (hasLure(this.look) ? 380 : 270) * dim, '#fffbe6']);
    }
    this.drawBubbles(g);
    this.drawEmotes(g, view);
    this.drawAim(g);
    this.drawDebrisAndSparkles(g);
    this.drawSnow(g, view);
    this.drawSurface(g, view);
    this.drawDarkness(g, view, lights);
    // Over the dark, so they show even in the deep.
    if (this.mode === 'play') this.drawBeastWarnings(g, view, t);
    if (--this.miniTick <= 0) { this.drawMinimap(); this.miniTick = 12; }
  }

  /**
   * Draw something tinted red (a hit): it's drawn into a small canvas the size
   * of its box on screen, red is painted over just its pixels, and the result
   * is put back where it belongs.
   */
  private drawFlashed(g: CanvasRenderingContext2D, x: number, y: number, r: number, amount: number, draw: (g: CanvasRenderingContext2D) => void) {
    const tm = g.getTransform();
    const k = tm.a;                       // world units → canvas pixels
    const size = Math.ceil(r * 2 * k);
    if (size < 2 || size > 2048) { draw(g); return; }
    const sx = Math.floor(tm.a * (x - r) + tm.e), sy = Math.floor(tm.d * (y - r) + tm.f);
    const c = (this.flashCanvas ??= document.createElement('canvas'));
    if (c.width < size || c.height < size) { c.width = Math.max(size, c.width); c.height = Math.max(size, c.height); }
    const o = c.getContext('2d')!;
    o.setTransform(1, 0, 0, 1, 0, 0);
    o.clearRect(0, 0, size, size);
    o.globalCompositeOperation = 'source-over';
    o.globalAlpha = 1;
    o.setTransform(tm.a, 0, 0, tm.d, tm.e - sx, tm.f - sy);
    draw(o);
    o.setTransform(1, 0, 0, 1, 0, 0);
    o.globalCompositeOperation = 'source-atop';
    o.fillStyle = `rgba(255,30,50,${amount})`;
    o.fillRect(0, 0, size, size);
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.drawImage(c, 0, 0, size, size, sx, sy, size, size);
    g.restore();
  }

  /** A red arrow at the edge of the screen for each beast coming at you from off it. */
  private drawBeastWarnings(g: CanvasRenderingContext2D, v: View, t: number) {
    const m = this.me, z = this.cam.z;
    const inset = 34 / z;
    for (const b of this.beasts) {
      if (this.inView(v, b.x, b.y, 20)) continue;
      const dx = m.x - b.x, dy = m.y - b.y, d = Math.hypot(dx, dy) || 1;
      if (d > 1500) continue;
      // Only the ones heading your way (or the Kraken, always).
      const closing = (b.vx * dx + b.vy * dy) / d;
      if (b.kind !== 'kraken' && closing < 60) continue;
      // Where the line from you to it leaves the screen.
      const ux = -dx / d, uy = -dy / d;
      const hw = (v.x1 - v.x0) / 2 - inset, hh = (v.y1 - v.y0) / 2 - inset;
      const k = Math.min(Math.abs(ux) > 1e-6 ? hw / Math.abs(ux) : Infinity, Math.abs(uy) > 1e-6 ? hh / Math.abs(uy) : Infinity);
      const cx = (v.x0 + v.x1) / 2, cy = (v.y0 + v.y1) / 2;
      const ax = cx + ux * k, ay = cy + uy * k;
      const near = Math.max(0, Math.min(1, (1500 - d) / 900));
      g.save();
      g.translate(ax, ay); g.rotate(Math.atan2(uy, ux)); g.scale(1 / z, 1 / z);
      g.globalAlpha = Math.min(1, 0.7 + near * 0.3 + Math.sin(t * 8) * 0.1 * near);
      const s = 12 + near * 6;
      g.fillStyle = '#ff5a6e'; g.strokeStyle = '#1e2633'; g.lineWidth = 2.5; g.lineJoin = 'round';
      g.beginPath(); g.moveTo(s, 0); g.lineTo(-s * 0.8, -s * 0.8); g.lineTo(-s * 0.4, 0); g.lineTo(-s * 0.8, s * 0.8); g.closePath(); g.fill(); g.stroke();
      g.restore();
    }
  }

  private inView(v: View, x: number, y: number, pad: number) {
    return x > v.x0 - pad && x < v.x1 + pad && y > v.y0 - pad && y < v.y1 + pad;
  }

  /** The rock arch: scenery behind the water, so a little darker; you swim in front of it. */
  private drawArch(g: CanvasRenderingContext2D, v: View) {
    this.world.archBoxes().forEach((ab, i) => {
      if (ab[2] < v.x0 || ab[0] > v.x1 || ab[3] < v.y0 || ab[1] > v.y1) return;
      g.save();
      g.lineJoin = 'round';
      g.beginPath(); this.world.traceArch(g, i); this.world.traceArchHole(g, i);
      g.fillStyle = '#6e7480';
      g.fill('evenodd');
      g.fillStyle = 'rgba(25,55,80,0.3)';
      g.fill('evenodd');
      g.strokeStyle = 'rgba(30,38,51,0.75)'; g.lineWidth = 5;
      g.stroke();
      g.restore();
    });
  }


  // Blocks, drawn a chunk at a time and kept until a block in the chunk changes.
  private drawBlocks(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    const want = this.cam.z * this.dpr;
    const scale = Math.pow(2, Math.round(Math.log2(want) * 2) / 2);
    if (scale !== this.chunkScale) { this.chunkScale = scale; this.chunks.clear(); }
    const size = CHUNK * B;
    const seam = 1 / (this.cam.z * this.dpr);
    const cx0 = Math.max(0, Math.floor(v.x0 / size)), cx1 = Math.floor(v.x1 / size);
    const cy0 = Math.max(0, Math.floor(v.y0 / size)), cy1 = Math.floor(v.y1 / size);
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        if (cx * CHUNK >= this.blocks.cols || cy * CHUNK >= this.blocks.rows) continue;
        const key = `${cx},${cy}`;
        const ver = this.blocks.chunkVersion.get(key) ?? 0;
        let ch = this.chunks.get(key);
        if (!ch || ch.ver !== ver) { ch = { c: this.renderChunk(cx, cy, scale), ver, used: 0 }; this.chunks.set(key, ch); }
        ch.used = this.time;
        g.drawImage(ch.c, cx * size, cy * size, size + seam, size + seam);
      }
    }
    const px = (size * scale) ** 2;
    const budget = Math.max(30, Math.floor(60e6 / px));
    if (this.chunks.size > budget) {
      const old = [...this.chunks.entries()].sort((a, b) => a[1].used - b[1].used).slice(0, this.chunks.size - budget);
      for (const [k] of old) this.chunks.delete(k);
    }
    // Glowing blocks light up the dark.
    const tx0 = Math.max(0, Math.floor(v.x0 / B)), tx1 = Math.min(this.blocks.cols - 1, Math.floor(v.x1 / B));
    const ty0 = Math.max(0, Math.floor(v.y0 / B)), ty1 = Math.min(this.blocks.rows - 1, Math.floor(v.y1 / B));
    for (let ty = ty0; ty <= ty1; ty++) for (let tx = tx0; tx <= tx1; tx++) {
      const id = this.blocks.now[ty * this.blocks.cols + tx];
      if (id === 11) lights.push([tx * B + B / 2, ty * B + B / 2, 230, '#fff4a8']);
      else if (id === 4 && (tx + ty) % 2 === 0) lights.push([tx * B + B / 2, ty * B + B / 2, 90, '#7ef9ff']);
    }
  }

  private renderChunk(cx: number, cy: number, scale: number): HTMLCanvasElement {
    const size = CHUNK * B;
    const c = document.createElement('canvas');
    c.width = c.height = Math.max(1, Math.round(size * scale));
    const g = c.getContext('2d')!;
    g.setTransform(scale, 0, 0, scale, -cx * size * scale, -cy * size * scale);
    const bl = this.blocks;
    const tx0 = cx * CHUNK, ty0 = cy * CHUNK;
    // Back walls first (where a block was mined out), then blocks, then edges.
    for (let ty = ty0; ty < ty0 + CHUNK && ty < bl.rows; ty++) for (let tx = tx0; tx < tx0 + CHUNK && tx < bl.cols; tx++) {
      const cur = bl.at(tx, ty), base = bl.baseAt(tx, ty);
      if (cur === 0 && base !== 0) {
        g.fillStyle = BLOCKS[base].dark;
        g.fillRect(tx * B, ty * B, B, B);
        g.fillStyle = 'rgba(0,0,0,0.18)';
        if ((tx + ty) % 2) g.fillRect(tx * B, ty * B, B, B);
      }
    }
    for (let ty = ty0; ty < ty0 + CHUNK && ty < bl.rows; ty++) for (let tx = tx0; tx < tx0 + CHUNK && tx < bl.cols; tx++) {
      const id = bl.at(tx, ty);
      if (id !== 0) drawBlock(g, id, tx, ty, bl.at(tx, ty - 1) === 0);
    }
    g.strokeStyle = INK;
    g.lineWidth = 2.6;
    g.lineCap = 'square';
    g.beginPath();
    for (let ty = ty0; ty < ty0 + CHUNK && ty < bl.rows; ty++) for (let tx = tx0; tx < tx0 + CHUNK && tx < bl.cols; tx++) {
      if (bl.at(tx, ty) === 0) continue;
      const x = tx * B, y = ty * B;
      if (bl.at(tx, ty - 1) === 0) { g.moveTo(x, y); g.lineTo(x + B, y); }
      if (bl.at(tx, ty + 1) === 0) { g.moveTo(x, y + B); g.lineTo(x + B, y + B); }
      if (bl.at(tx - 1, ty) === 0) { g.moveTo(x, y); g.lineTo(x, y + B); }
      if (bl.at(tx + 1, ty) === 0) { g.moveTo(x + B, y); g.lineTo(x + B, y + B); }
    }
    g.stroke();
    return c;
  }

  private drawDecorations(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    const list = this.decor;
    let lo = 0, hi = list.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (list[mid].x < v.x0 - 950) lo = mid + 1; else hi = mid; }
    for (let i = lo; i < list.length && list[i].x < v.x1 + 950; i++) {
      const d = list[i];
      if (d.cell && this.blocks.at(d.cell[0], d.cell[1]) === 0) continue;   // its block was mined
      const reach = decorReach(d);
      if (d.x + reach < v.x0 || d.x - reach > v.x1 || d.y + reach < v.y0 || d.y - reach > v.y1) continue;
      drawDecor(g, d, this.time, lights);
    }
  }

  /** Sealed temple doors (this student's locked ones), with glowing runes. */
  private drawGates(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    for (const gt of this.world.gates) {
      if (!this.inView(v, gt.x + gt.w / 2, gt.y, 200)) continue;
      const open = this.mode === 'play' && this.gatesOpen.has(`gate${gt.i}`);
      if (open) continue;
      const near = this.target?.kind === 'gate' && this.target.i === gt.i;
      drawGate(g, gt, this.time, near);
      lights.push([gt.x + gt.w / 2, gt.y + gt.h / 2, 140, '#7ef9ff']);
    }
  }

  private drawBubbles(g: CanvasRenderingContext2D) {
    g.save();
    g.strokeStyle = 'rgba(255,255,255,0.75)'; g.lineWidth = 1.5;
    for (const b of this.bubbles) {
      const age = (this.time - b.t) / 1.4;
      g.globalAlpha = 1 - age;
      g.beginPath(); g.arc(b.x, b.y, b.r * (1 + age * 0.4), 0, Math.PI * 2); g.stroke();
    }
    for (const p of this.puffs) {
      const age = (this.time - p.t) / 0.6;
      g.globalAlpha = 0.6 * (1 - age);
      g.strokeStyle = p.color; g.lineWidth = 4 * (1 - age) + 1;
      g.beginPath(); g.arc(p.x, p.y, p.r * (0.6 + age * 1.2), 0, Math.PI * 2); g.stroke();
    }
    g.restore();
  }

  /** Emotes over fish, for 2.5 s: mine and classmates'. */
  private drawEmotes(g: CanvasRenderingContext2D, v: View) {
    for (const [id, em] of this.emotes) {
      const age = this.time - em.t;
      if (age > 2.5) { this.emotes.delete(id); continue; }
      const who = id === 'me' ? (this.mode === 'play' ? this.me : null) : this.peers.get(id);
      if (!who || !this.inView(v, who.x, who.y, 100)) continue;
      const pop = Math.min(1, age * 6), rise = age * 16, fade = age > 2 ? 1 - (age - 2) * 2 : 1;
      g.save();
      g.globalAlpha = fade;
      g.translate(who.x, who.y - 64 - rise);
      g.scale(pop, pop);
      g.fillStyle = 'rgba(255,255,255,0.95)'; g.strokeStyle = INK; g.lineWidth = 2;
      g.beginPath(); g.arc(0, 0, 22, 0, Math.PI * 2); g.fill(); g.stroke();
      g.beginPath(); g.moveTo(-6, 20); g.lineTo(0, 30); g.lineTo(6, 20); g.fill();
      g.font = '24px system-ui'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(em.e, 0, 1);
      g.restore();
    }
  }

  private drawChests(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    for (const c of this.chests) {
      if (!this.inView(v, c.x, c.y, 80)) continue;
      const open = this.mode === 'play' && this.opened.has(c.id);
      const near = this.target?.kind === 'chest' && this.target.id === c.id;
      drawChest(g, c.x, c.y, c.tier, open, this.time, near);
      if (!open && c.tier > 1) lights.push([c.x, c.y - 20, c.tier === 3 ? 170 : 110, c.tier === 3 ? '#ffd23d' : '#9ff7ff']);
    }
  }

  /** Habitats: the fish that live in each, swimming inside what was built, and a label up close. */
  private drawHabitats(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    const byKey = new Map((this.data.habitats ?? []).map(h => [`${h.tx},${h.ty}`, h]));
    for (const [tx, ty] of this.beaconList()) {
      const bx = tx * B + B / 2, by = ty * B + B / 2;
      if (!this.inView(v, bx, by, 500)) continue;
      const st = this.habitat(tx, ty);
      const h = byKey.get(`${tx},${ty}`);
      const cells = st.inside.length ? st.inside : [[tx, ty - 1] as [number, number]];
      (h?.residents ?? []).forEach((f, i) => {
        const sprite = spriteFor(f.species);
        const sp = speciesById(f.species);
        if (!sprite) return;
        // Swim from one open cell inside to another, a leg every few seconds.
        const h1 = hash(f.id, 1), leg = 3 + h1 * 3;
        const k = (this.time + h1 * 50) / leg, n = Math.floor(k), u = k - n, e = u * u * (3 - 2 * u);
        const at = (j: number) => { const [cx, cy] = cells[Math.floor(cellHash(j, i, Math.floor(h1 * 1e4)) * cells.length)]; return { x: cx * B + B / 2, y: cy * B + B / 2 }; };
        const a = at(n), b = at(n + 1);
        const x = a.x + (b.x - a.x) * e, y = a.y + (b.y - a.y) * e;
        const longest = 46 * sp.size;
        const w = sprite.w >= sprite.h ? longest : (longest * sprite.w) / sprite.h;
        const facing: 1 | -1 = b.x > a.x ? -1 : 1;
        if (sp.look.glow) lights.push([x, y, 100, sp.look.glow]);
        drawIdle(g, sprite, sp.profile, this.time + h1 * 10, { x, y, width: w, facing, step: 2 / this.cam.z });
      });
      // A label when you're close: what it is, and how full.
      const near = this.mode === 'spectate' || Math.hypot(bx - this.me.x, by - this.me.y) < 360;
      if (!near) continue;
      const c = insideCentre(st, tx, ty);
      const info = st.type ? HABITAT_INFO[st.type] : null;
      const label = !st.enclosed ? '🧱 Close it in to make a habitat'
        : info ? `${info.icon} ${info.name} · ${(h?.residents.length ?? 0)}/${st.capacity} fish`
          : '🔍 Not a habitat yet: build with one kind of block';
      const owner = h?.owner && !(h.mine && this.mode === 'play') ? ` · ${h.owner}` : '';
      g.save();
      g.font = `600 ${this.mode === 'spectate' ? 18 : 13}px system-ui`;
      const text = label + owner;
      const tw = Math.min(320, g.measureText(text).width) + 18;
      const ly = Math.min(...cells.map(([, cy]) => cy)) * B - 30;
      g.fillStyle = 'rgba(255,240,252,0.95)'; g.strokeStyle = INK; g.lineWidth = 2;
      g.beginPath(); g.roundRect(c.x - tw / 2, ly - 12, tw, 24, 8); g.fill(); g.stroke();
      g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(text, c.x, ly, 320);
      g.restore();
    }
  }

  /** Fish houses anywhere in the ocean, their fish, and (when names are on) whose they are. */
  private drawHouses(g: CanvasRenderingContext2D, v: View) {
    const t = this.time;
    for (const h of this.data.houses) {
      const size = HOUSES[h.kind].size;
      if (!this.inView(v, h.x, h.y - size / 2, size * 2)) continue;
      // Sit on whatever is under it now (a classmate may have dug the ground away).
      const ground = this.blocks.groundBelow(h.x, h.y - 20) ?? h.y;
      drawHouse(g, h.kind, h.x, ground, t);
      const near = this.target?.kind === 'house' && this.target.id === h.id;
      const door = houseDoor(h.kind);
      if (near) {
        g.save();
        g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = 3; g.setLineDash([6, 6]); g.lineDashOffset = -t * 20;
        g.beginPath(); g.arc(h.x + door.dx, ground + door.dy, 26, 0, Math.PI * 2); g.stroke();
        g.restore();
      }
      const now = Date.now() / 1000;
      h.fish.forEach((f, i) => {
        // Fish come and go: some of the time they're inside (in the room, not here).
        const where = fishWhere(f.id, now);
        if (where.inside) return;
        const sprite = spriteFor(f.species);
        if (!sprite) return;
        const sp = speciesById(f.species);
        const h1 = hash(f.id, 1), h2 = hash(f.id, 2);
        const a = (Math.PI * 2 * t) / (6 + h1 * 6) + h2 * 6.28;
        const x = h.x + Math.sin(a) * (size * 0.6 + 12 * i);
        const longest = 46 * sp.size;
        const w = sprite.w >= sprite.h ? longest : (longest * sprite.w) / sprite.h;
        const y = sp.habitat === 'bottom' ? ground - (w * sprite.h) / sprite.w / 2 : ground - size * 0.9 - 14 * (i % 3) + Math.sin(a * 1.7) * 8;
        let facing: 1 | -1 = sp.habitat === 'swim' && Math.cos(a) > 0 ? -1 : 1;
        // Swimming through the doorway: drift to it and shrink in.
        const k = where.door;
        const dx = h.x + door.dx, dy = ground + door.dy;
        if (k > 0.05) facing = dx < x ? 1 : -1;
        g.save();
        if (k > 0) g.globalAlpha = 1 - k * 0.85;
        drawIdle(g, sprite, sp.profile, t + h1 * 10, { x: x + (dx - x) * k, y: y + (dy - y) * k, width: w * (1 - k * 0.6), facing, step: 2 / this.cam.z });
        g.restore();
      });
      const label = h.mine && this.mode === 'play' ? 'Yours' : h.owner;
      if (label) {
        g.save();
        g.font = `600 ${this.mode === 'spectate' ? 18 : 13}px system-ui`;
        const tw = Math.min(150, g.measureText(label).width) + 16;
        g.fillStyle = h.mine && this.mode === 'play' ? 'rgba(255,224,102,0.95)' : 'rgba(243,226,192,0.95)';
        g.strokeStyle = INK; g.lineWidth = 2;
        g.beginPath(); g.roundRect(h.x - tw / 2, ground - size - 30, tw, this.mode === 'spectate' ? 28 : 22, 7); g.fill(); g.stroke();
        g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(label, h.x, ground - size - (this.mode === 'spectate' ? 16 : 19), 150);
        g.restore();
      }
    }

    // A ghost of the house being placed, where it would stand.
    if (this.tool.kind === 'house' && this.hover && this.mode === 'play') {
      const w = this.toWorld(this.hover.x, this.hover.y);
      const spot = this.houseSpot(w.x, w.y);
      if (spot) drawHouse(g, this.tool.item, spot.x, spot.y, t, true);
    }
  }

  private drawWild(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    for (const f of this.wild) {
      if (!this.inView(v, f.x, f.y, 80)) continue;
      const sprite = spriteFor(f.species);
      if (!sprite) continue;
      const sp = speciesById(f.species);
      const longest = 58 * sp.size;
      const w = sprite.w >= sprite.h ? longest : (longest * sprite.w) / sprite.h;
      const fade = Math.min(1, (this.time - f.born) * 2);
      if (sp.look.glow) lights.push([f.x, f.y, 120 * sp.size, sp.look.glow]);
      if (sp.rarity === 'epic' || sp.rarity === 'legendary' || sp.rarity === 'mythic') {
        lights.push([f.x, f.y, sp.rarity === 'mythic' ? 130 : 90, sp.rarity === 'mythic' ? '#ff7af5' : sp.rarity === 'legendary' ? '#ffd23d' : '#d9a6ff']);
        g.save();
        g.fillStyle = sp.rarity === 'legendary' ? '#ffe680' : sp.rarity === 'mythic' ? '#ffb3f5' : '#e6c6ff';
        for (let k = 0; k < 4; k++) {
          const a = this.time * 2 + k * 1.57 + f.id, rr = 30 + Math.sin(this.time * 3 + k) * 6;
          const sx = f.x + Math.cos(a) * rr, sy = f.y + Math.sin(a) * rr * 0.6, sz = 2 + Math.sin(this.time * 5 + k) * 1.5;
          g.beginPath(); g.moveTo(sx, sy - sz * 2); g.lineTo(sx + sz * 0.6, sy); g.lineTo(sx, sy + sz * 2); g.lineTo(sx - sz * 0.6, sy); g.closePath(); g.fill();
        }
        g.restore();
      }
      g.save();
      g.globalAlpha = fade;
      const facing: 1 | -1 = sp.habitat === 'swim' && f.vx > 0 ? -1 : 1;
      drawIdle(g, sprite, sp.profile, this.time * (f.dash > 0 ? 2.5 : 1) + f.id, { x: f.x, y: f.y, width: w, facing, step: 2 / this.cam.z });
      g.restore();
    }
  }

  /** Classmates in the ocean right now, with a name tag when names are allowed. */
  private drawPeers(g: CanvasRenderingContext2D, v: View) {
    for (const p of this.peers.values()) {
      if (!p.look || !this.inView(v, p.x, p.y, 80)) continue;
      const fade = p.gone ? Math.max(0, 1 - (this.time - p.gone)) : Math.min(1, (this.time - p.seen + 1) * 2);
      g.save();
      g.globalAlpha = fade;
      drawAvatar(g, p.look, { x: p.x, y: p.y, size: 60, facing: p.facing, effort: p.effort, t: this.time, seed: p.id.length,
        tilt: Math.max(-0.5, Math.min(0.5, Math.atan2(p.vy, Math.abs(p.vx) + 60))) });
      const name = this.names.get(p.id) ?? null;
      if (name) {
        g.font = '600 13px system-ui';
        const w = Math.min(140, g.measureText(name).width) + 14;
        g.fillStyle = 'rgba(15,40,55,0.7)';
        g.beginPath(); g.roundRect(p.x - w / 2, p.y - 54, w, 20, 10); g.fill();
        g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(name, p.x, p.y - 44, 140);
      }
      g.restore();
    }
  }

  /** The net, swinging in front of the fish. */
  private drawNet(g: CanvasRenderingContext2D) {
    const m = this.me;
    const swing = this.net.swing;
    const showIdle = this.tool.kind === 'net';
    if (swing < 0 && !showIdle) return;
    const angle = swing < 0 ? -0.9 : -1.4 + Math.sin(swing * Math.PI) * 2.2;
    const gold = this.gear.goldenNet;
    g.save();
    g.translate(m.x + m.facing * 18, m.y + 4);
    g.scale(m.facing, 1);
    g.rotate(angle);
    g.lineCap = 'round';
    g.strokeStyle = '#7a5434'; g.lineWidth = 4;
    g.beginPath(); g.moveTo(0, 0); g.lineTo(38, 0); g.stroke();
    g.strokeStyle = gold ? '#ffd23d' : '#e8e3d6'; g.lineWidth = 3;
    g.beginPath(); g.ellipse(52, 0, gold ? 18 : 15, gold ? 13 : 11, 0, 0, Math.PI * 2); g.stroke();
    g.strokeStyle = 'rgba(255,255,255,0.5)'; g.lineWidth = 1;
    for (let i = -2; i <= 2; i++) { g.beginPath(); g.moveTo(52 + i * 6, -10); g.lineTo(52 + i * 6, 10); g.stroke(); }
    g.restore();
  }

  /** Outline the block the pointer is on when mining or building. */
  private drawAim(g: CanvasRenderingContext2D) {
    if (this.mode !== 'play' || !this.hover || (this.tool.kind !== 'pick' && this.tool.kind !== 'block')) return;
    const w = this.toWorld(this.hover.x, this.hover.y);
    const tx = Math.floor(w.x / B), ty = Math.floor(w.y / B);
    const cx = tx * B + B / 2, cy = ty * B + B / 2;
    const near = Math.hypot(cx - this.me.x, cy - this.me.y) <= REACH;
    const cur = this.blocks.at(tx, ty);
    const ok = near && (this.tool.kind === 'pick'
      ? this.gear.pickaxe > 0 && cur !== 0 && cur !== 99 && !(BLOCKS[cur]?.drill && !this.gear.drill)
      : this.gear.hammer > 0 && cur === 0);
    g.save();
    g.lineWidth = 3;
    g.strokeStyle = ok ? 'rgba(255,255,255,0.95)' : 'rgba(255,90,90,0.8)';
    g.strokeRect(tx * B + 1.5, ty * B + 1.5, B - 3, B - 3);
    if (ok && this.tool.kind === 'block') {
      const def = blockByItem(this.tool.item);
      if (def) { g.globalAlpha = 0.45; drawBlock(g, def.id, tx, ty, false); }
    }
    g.restore();
  }

  private drawDebrisAndSparkles(g: CanvasRenderingContext2D) {
    for (const p of this.debris) {
      g.globalAlpha = 1 - (this.time - p.t) / 0.8;
      g.fillStyle = p.color;
      g.fillRect(p.x - 3, p.y - 3, 6, 6);
    }
    g.globalAlpha = 1;
    this.sparkles = this.sparkles.filter(s => this.time - s.t < 1);
    for (const s of this.sparkles) {
      const k = this.time - s.t;
      g.save();
      g.globalAlpha = 1 - k;
      g.strokeStyle = s.color ?? '#fff6b0'; g.lineWidth = 3;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2, r1 = 10 + k * 40, r2 = 18 + k * 60;
        g.beginPath(); g.moveTo(s.x + Math.cos(a) * r1, s.y + Math.sin(a) * r1); g.lineTo(s.x + Math.cos(a) * r2, s.y + Math.sin(a) * r2); g.stroke();
      }
      g.restore();
    }
  }

  private drawSnow(g: CanvasRenderingContext2D, v: View) {
    const cell = 220;
    g.fillStyle = 'rgba(255,255,255,0.35)';
    for (let cx = Math.floor(v.x0 / cell); cx <= Math.floor(v.x1 / cell); cx++) {
      for (let cy = Math.floor(v.y0 / cell); cy <= Math.floor(v.y1 / cell); cy++) {
        for (let i = 0; i < 2; i++) {
          const h1 = hash(`${cx},${cy},${i}`, 7), h2 = hash(`${cx},${cy},${i}`, 9);
          const x = cx * cell + h1 * cell + Math.sin(this.time * 0.4 + h2 * 9) * 12;
          const y = cy * cell + ((h2 * cell + this.time * 8) % cell);
          if (y < 0) continue;
          g.beginPath(); g.arc(x, y, 1.6 + h1 * 1.4, 0, Math.PI * 2); g.fill();
        }
      }
    }
  }

  private drawSurface(g: CanvasRenderingContext2D, v: View) {
    if (v.y0 > 20) return;
    g.save();
    g.strokeStyle = 'rgba(255,255,255,0.85)';
    g.lineWidth = 4;
    g.beginPath();
    for (let x = Math.floor(v.x0 / 20) * 20; x <= v.x1 + 20; x += 20) {
      const y = Math.sin(x / 70 + this.time * 1.5) * 4 + Math.sin(x / 23 + this.time) * 2;
      if (x === Math.floor(v.x0 / 20) * 20) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke();
    g.restore();
  }

  // Deep water and caves get dark; lights (you, lamps, crystals, pearls, vents) cut through.
  private drawDarkness(g: CanvasRenderingContext2D, v: View, lights: [number, number, number, string][]) {
    const strength = this.mode === 'spectate' ? 0.35 : 1;
    const d = this.darkness, dg = d.getContext('2d')!;
    const k = 0.5 * this.dpr * this.cam.z;
    dg.setTransform(1, 0, 0, 1, 0, 0);
    dg.globalCompositeOperation = 'source-over';
    dg.clearRect(0, 0, d.width, d.height);
    dg.setTransform(k, 0, 0, k, (-this.cam.x * this.cam.z + this.vw / 2) * this.dpr * 0.5, (-this.cam.y * this.cam.z + this.vh / 2) * this.dpr * 0.5);
    const deep = dg.createLinearGradient(0, 700, 0, this.world.h);
    deep.addColorStop(0, `rgba(2,10,26,${0.6 * nightness()})`);
    deep.addColorStop(0.45, `rgba(2,10,26,${0.45 * strength})`);
    deep.addColorStop(1, `rgba(2,10,26,${0.8 * strength})`);
    dg.fillStyle = deep;
    dg.fillRect(v.x0 - 10, v.y0 - 10, v.x1 - v.x0 + 20, v.y1 - v.y0 + 20);
    if (v.y1 > 1300) {
      // Under the sea floor (but not its top layer) it's dark: caves and dug tunnels feel underground.
      dg.save();
      dg.translate(0, 70);
      dg.beginPath(); this.world.traceFloor(dg, v.x0 - 50, v.x1 + 50);
      dg.translate(0, -70);
      dg.clip();
      dg.fillStyle = `rgba(2,6,16,${0.5 * strength})`;
      dg.fillRect(v.x0 - 10, v.y0 - 10, v.x1 - v.x0 + 20, v.y1 - v.y0 + 20);
      dg.fillStyle = `rgba(2,6,16,${0.45 * strength})`;
      dg.beginPath(); this.world.traceCaveCarves(dg, 12); dg.fill();
      dg.strokeStyle = dg.fillStyle; this.world.strokeTunnels(dg, 12);
      dg.restore();
      dg.fillStyle = `rgba(2,6,16,${0.55 * strength})`;
      dg.beginPath(); this.world.traceCabin(dg, 6); dg.fill();
    }
    dg.globalCompositeOperation = 'destination-out';
    for (const [x, y, r] of lights) {
      if (!this.inView(v, x, y, r)) continue;
      const rg = dg.createRadialGradient(x, y, r * 0.15, x, y, r);
      rg.addColorStop(0, 'rgba(0,0,0,1)');
      rg.addColorStop(1, 'rgba(0,0,0,0)');
      dg.fillStyle = rg;
      dg.beginPath(); dg.arc(x, y, r, 0, Math.PI * 2); dg.fill();
    }
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.drawImage(d, 0, 0, this.canvas.width, this.canvas.height);
    g.globalCompositeOperation = 'lighter';
    g.setTransform(this.cam.z * this.dpr, 0, 0, this.cam.z * this.dpr, (-this.cam.x * this.cam.z + this.vw / 2) * this.dpr, (-this.cam.y * this.cam.z + this.vh / 2) * this.dpr);
    for (const [x, y, r, col] of lights) {
      if (col === '#fffbe6' || !this.inView(v, x, y, r)) continue;
      const rg = g.createRadialGradient(x, y, 0, x, y, r * 0.6);
      rg.addColorStop(0, col + '40');
      rg.addColorStop(1, col + '00');
      g.fillStyle = rg;
      g.beginPath(); g.arc(x, y, r * 0.6, 0, Math.PI * 2); g.fill();
    }
    g.restore();
  }

  private drawMinimap() {
    const c = this.minimap;
    if (!c) return;
    const g = c.getContext('2d')!;
    const r = c.getBoundingClientRect();
    const dpr = this.dpr;
    if (c.width !== Math.round(r.width * dpr)) { c.width = Math.round(r.width * dpr); c.height = Math.round(r.height * dpr); }
    const W = c.width, H = c.height, sx = W / this.world.w, sy = H / this.world.h;
    if (!W || !H) return;   // hidden (small screens)
    if (!this.minimapBase || this.minimapBase.width !== W || this.minimapVer !== this.editVer) {
      this.minimapBase = renderMinimapBase(this.blocks, W, H);
      this.minimapVer = this.editVer;
    }
    g.clearRect(0, 0, W, H);
    g.drawImage(this.minimapBase, 0, 0);
    if (this.mode === 'play') {
      g.save();
      g.fillStyle = 'rgba(8,16,30,0.92)';
      g.fillRect(0, 0, W, H);
      g.globalCompositeOperation = 'destination-out';
      for (const i of this.revealed) {
        const b = this.world.bounds[i]?.[1];
        if (b) g.fillRect(b[0] * sx, b[1] * sy, (b[2] - b[0]) * sx, (b[3] - b[1]) * sy);
      }
      g.restore();
    }
    for (const h of this.data.houses) {
      g.beginPath(); g.arc(h.x * sx, (h.y - 20) * sy, h.mine ? 4 * dpr : 2.8 * dpr, 0, Math.PI * 2);
      g.fillStyle = h.mine ? '#ffd23d' : '#ffffff'; g.fill();
    }
    if (this.mode === 'play') {
      g.beginPath(); g.arc(this.me.x * sx, this.me.y * sy, 4.5 * dpr, 0, Math.PI * 2);
      g.fillStyle = '#ff5c7a'; g.fill(); g.strokeStyle = '#fff'; g.lineWidth = 1.5 * dpr; g.stroke();
    } else {
      const hw = this.vw / 2 / this.cam.z, hh = this.vh / 2 / this.cam.z;
      g.strokeStyle = '#fff'; g.lineWidth = 1.5 * dpr;
      g.strokeRect((this.cam.x - hw) * sx, (this.cam.y - hh) * sy, hw * 2 * sx, hh * 2 * sy);
    }
  }
}

// ── Drawing one block ───────────────────────────────────────────────────────

function drawBlock(g: CanvasRenderingContext2D, id: number, tx: number, ty: number, topExposed: boolean) {
  const def = BLOCKS[id];
  if (!def) return;
  const x = tx * B, y = ty * B;
  const v = cellHash(tx, ty, 1);
  g.fillStyle = def.color;
  g.globalAlpha = def.see ? 0.55 : 1;
  g.fillRect(x, y, B, B);
  g.globalAlpha = 1;
  // A little variation so a wall of the same block doesn't look flat.
  g.fillStyle = v < 0.5 ? `rgba(255,255,255,${(0.5 - v) * 0.16})` : `rgba(0,0,0,${(v - 0.5) * 0.16})`;
  g.fillRect(x, y, B, B);
  const h = (i: number) => cellHash(tx, ty, 10 + i);
  switch (id) {
    case 1: case 6: case 99: {
      g.fillStyle = id === 1 ? 'rgba(150,120,70,0.35)' : 'rgba(255,255,255,0.1)';
      for (let i = 0; i < 4; i++) g.fillRect(x + 3 + h(i) * (B - 8), y + 3 + h(i + 4) * (B - 8), 3, 3);
      break;
    }
    case 2: {
      g.strokeStyle = 'rgba(30,38,51,0.25)'; g.lineWidth = 1.5;
      g.beginPath(); g.moveTo(x + 4 + h(1) * 10, y + 6); g.lineTo(x + 12 + h(2) * 10, y + 16); g.lineTo(x + 8 + h(3) * 16, y + 26); g.stroke();
      break;
    }
    case 3: {
      g.fillStyle = 'rgba(255,255,255,0.35)';
      for (let i = 0; i < 3; i++) { g.beginPath(); g.arc(x + 6 + h(i) * 20, y + 6 + h(i + 3) * 20, 2.5, 0, Math.PI * 2); g.fill(); }
      break;
    }
    case 4: {
      g.fillStyle = 'rgba(255,255,255,0.55)';
      g.beginPath(); g.moveTo(x + 16, y + 4); g.lineTo(x + 26, y + 16); g.lineTo(x + 16, y + 28); g.lineTo(x + 6, y + 16); g.closePath(); g.fill();
      break;
    }
    case 5: case 10: {
      g.strokeStyle = 'rgba(30,38,51,0.3)'; g.lineWidth = 1.5;
      for (const yy of [10.5, 21.5]) { g.beginPath(); g.moveTo(x, y + yy); g.lineTo(x + B, y + yy); g.stroke(); }
      g.beginPath(); g.moveTo(x + 6 + h(1) * 20, y); g.lineTo(x + 6 + h(1) * 20, y + 10.5); g.stroke();
      break;
    }
    case 7: {
      g.strokeStyle = 'rgba(255,255,255,0.8)'; g.lineWidth = 2;
      g.beginPath(); g.moveTo(x + 6, y + 14); g.lineTo(x + 14, y + 6); g.moveTo(x + 10, y + 24); g.lineTo(x + 24, y + 10); g.stroke();
      break;
    }
    case 8: case 9: {
      g.strokeStyle = 'rgba(30,38,51,0.35)'; g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(x, y + 16); g.lineTo(x + B, y + 16);
      g.moveTo(x + 16, y); g.lineTo(x + 16, y + 16);
      g.moveTo(x + 6, y + 16); g.lineTo(x + 6, y + B);
      g.moveTo(x + 24, y + 16); g.lineTo(x + 24, y + B);
      g.stroke();
      break;
    }
    case 11: {
      const rg = g.createRadialGradient(x + 16, y + 16, 2, x + 16, y + 16, 14);
      rg.addColorStop(0, '#ffffff'); rg.addColorStop(1, 'rgba(255,244,168,0)');
      g.fillStyle = rg; g.fillRect(x, y, B, B);
      g.strokeStyle = 'rgba(30,38,51,0.4)'; g.lineWidth = 2; g.strokeRect(x + 5, y + 5, B - 10, B - 10);
      break;
    }
  }
  if (topExposed && !def.see) {
    g.fillStyle = 'rgba(255,255,255,0.22)';
    g.fillRect(x, y, B, 4);
  }
}

/** The minimap's background: water and blocks. */
function renderMinimapBase(bl: Blocks, W: number, H: number): HTMLCanvasElement {
  const src = document.createElement('canvas');
  src.width = bl.cols; src.height = bl.rows;
  const sg = src.getContext('2d')!;
  const img = sg.createImageData(bl.cols, bl.rows);
  for (let y = 0; y < bl.rows; y++) {
    const depth = y / bl.rows;
    for (let x = 0; x < bl.cols; x++) {
      const i = (y * bl.cols + x) * 4;
      if (bl.now[y * bl.cols + x]) { img.data[i] = 120 - depth * 60; img.data[i + 1] = 112 - depth * 55; img.data[i + 2] = 100 - depth * 40; }
      else { img.data[i] = 60 - depth * 50; img.data[i + 1] = 150 - depth * 110; img.data[i + 2] = 200 - depth * 130; }
      img.data[i + 3] = 255;
    }
  }
  sg.putImageData(img, 0, 0);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  g.imageSmoothingEnabled = true;
  g.drawImage(src, 0, 0, W, H);
  return c;
}
